/**
 * Radia Code App – UI-Texte für den Custom-Tab.
 * Wird in i18n/index.ts in alle Locales gemerged (fehlende Sprache → EN).
 */
export const radiacodeEn = {
    intro: {
        title: 'What is Radia Code App?',
        text: 'Radia Code App is a Garmin Connect IQ watch app that talks directly to a RadiaCode radiation meter over Bluetooth Low Energy. Live readings do not need a phone or internet. You see dose rate, count rate, a pulse chart against the local background, device temperature, and you can optionally record a Garmin activity with radiation data.',
        lang: 'The app follows the language of your watch. Settings you change on the watch are stored on the watch.',
    },
    connect: {
        title: 'Connect your RadiaCode',
        warning: 'A RadiaCode can be connected to only one device at a time. That includes a phone. If the official RadiaCode phone app (or any other Bluetooth client) is connected, the watch cannot use the meter. Close or disconnect that app first, then try again from the watch.',
        warningExtra: 'The same applies to another watch, a computer, or a tablet. Leave the RadiaCode unpaired / disconnected everywhere else before you connect it here.',
        stepsTitle: 'How to pair',
        step1: 'Turn the RadiaCode on and keep it close to the watch (a few metres is enough).',
        step2: 'Open Radia Code App on the watch. It starts scanning immediately and shows “Searching…”.',
        step3: 'When your meter appears in the list, highlight it with UP / DOWN and press START or SELECT. On a touchscreen you can tap the name.',
        step4: 'The watch stays on “Connecting…” until the first live measurement arrives. This can take several seconds, especially on Instinct watches. Do not press START while it is still connecting.',
        step5: 'After a successful connection the device name is saved. Next time the app opens it will try to reconnect automatically.',
        forget: 'If you want a different meter, open the menu and choose Forget device. That turns auto-connect off. Rescan starts a new search.',
        notFound: 'If the meter does not appear or connecting fails, it is almost always still held by the phone app or another Bluetooth client. Disconnect there, wait a moment, then rescan.',
    },
    search: {
        title: 'Search screen',
        titleLine: 'The title bar shows “Radia Code”.',
        list: 'Below that is the list of nearby RadiaCode devices found over Bluetooth.',
        instinct: 'On Instinct (MIP) watches, button hints sit on the bezel: Menu at mid-left, Connect at the start button, Back at lower-right.',
        empty: 'While scanning you see “Searching…”. If nothing is found, rescan from the menu.',
    },
    measure: {
        title: 'Measurement screen',
        intro: 'Once the first sample arrives, the live view replaces the connecting card.',
        dose: 'Large dose-rate value with the unit you chose (Sievert or Roentgen, auto / µSv/h / nSv/h).',
        chart: 'Pulse chart of recent count-rate samples. The fill is drawn against the local background so a rise is easy to see.',
        counts: 'Count rate as CPS (counts per second) or CPM (counts per minute).',
        footer: 'Footer: RadiaCode temperature and the detected local background level.',
        time: 'Current time is shown in the header (on Instinct in the sub-display / pocket).',
        ring: 'On round colour displays a level ring (or a small bar on other shapes) shows how the current rate compares to background.',
        colour: 'Chart and value colour follow the rate relative to the local background. Auto Detect Radiation treats roughly twice the background as elevated and can trigger an alert.',
        rec: 'While an activity is recording, the header shows REC plus elapsed time instead of the clock, and the footer shows the accumulated total dose.',
    },
    controls: {
        title: 'Buttons (connected)',
        menu: 'Open settings (units, GPS, screen, alarms). On the search screen the same button opens the scan menu.',
        start: 'Start or stop the optional activity (START/SELECT, or tap the Start/Stop label in the upper-right). A tap elsewhere on the measurement screen does nothing. Blocked while the watch is still connecting.',
        back: 'Leave the app. If an activity is recording, the stop menu is shown first (Save / Discard / Continue).',
        select: 'On the search screen, connect to the highlighted device.',
    },
    menu: {
        title: 'Menu items',
        scanTitle: 'Search / scan menu',
        scanIntro: 'Available while you are not connected. The title is the app name.',
        forget: 'Forget device — disable auto-connect and clear the saved meter (only if one was saved).',
        rescan: 'Rescan — start a new Bluetooth search.',
        connectedTitle: 'Connected menu',
        connectedIntro: 'Same settings as below, plus Disconnect to drop the current meter and return to the search list.',
        settingsTitle: 'Settings (both menus)',
        dose: 'Dose unit — Sievert (Sv) or Roentgen (R).',
        rate: 'Rate unit — Auto, µSv/h, or nSv/h.',
        count: 'Count unit — CPS or CPM.',
        gps: 'Log GPS position during activity — when on, a recorded activity includes a GPS track. When off, the session is stored as indoor / training without GPS.',
        keep: 'Keep Screen active — keeps the display / backlight on so you can glance at the rate without waking the watch.',
        auto: 'Auto Detect Radiation — alert when the rate is clearly elevated versus the local background (about 2×).',
        threshold: 'Threshold Alarm — absolute dose-rate alarm: Off, 0.50, 1.00, 2.00, 5.00, or 10.0 µSv/h.',
    },
    activity: {
        title: 'Optional activity recording',
        intro: 'You do not have to record anything to use the live display. Recording is optional and creates a normal Garmin activity you can review in Garmin Connect.',
        start: 'On the measurement screen press START (or tap the Start/Activity corner on a touchscreen). Confirm “Start activity?”. The header switches to REC and a running timer.',
        gps: 'If “Log GPS position during activity” is on, the watch looks for a GPS fix. You may see “GPS…” until a position is available, then “GPS”.',
        live: 'While recording, the footer shows the accumulated total dose for this session.',
        stopTitle: 'Stop menu',
        save: 'End the session and write the FIT file to the watch (then syncs to Garmin Connect).',
        discard: 'End without saving.',
        continue: 'Close the menu and keep recording.',
        fitTitle: 'What is stored',
        fitCpm: 'CPM — count rate over time (chart in Garmin Connect).',
        fitRate: 'Dose rate in µSv/h over time (chart).',
        fitTotal: 'Total dose in µSv for the whole session (activity summary).',
        fitLap: 'Lap dose in µSv (per lap; device auto-lap such as 1 km applies if enabled on the watch).',
        fitGps: 'GPS track, only if the GPS setting was on when you started.',
        close: 'If you close the app while a session is still recording, the app saves the activity so the data is not lost.',
    },
    instinct: {
        title: 'Instinct (MIP) notes',
        buttons: 'There is no touchscreen. Use the labelled bezel hints: Menu, Connect / Start, Back.',
        connect: 'Connecting can take longer than on AMOLED watches. Stay on the connecting card until the first value appears; the app will not open the live view earlier.',
        chart: 'The pulse chart is drawn inverted for the monochrome display so the waveform stays readable.',
        time: 'Time (and GPS / REC status during an activity) uses the small sub-display.',
    },
    supportBefore: 'Questions or issues? Please use the',
    supportLink: 'contact form',
    supportAfter: '.',
};
export const radiacodeDe = {
    intro: {
        title: 'Was ist Radia Code App?',
        text: 'Radia Code App ist eine Garmin-Connect-IQ-Uhr-App, die direkt per Bluetooth Low Energy mit einem RadiaCode-Strahlungsmessgerät spricht. Für Live-Werte brauchst du kein Handy und kein Internet. Du siehst Dosisleistung, Zählrate, einen Impulschart gegen den lokalen Hintergrund, die Gerätetemperatur und kannst optional eine Garmin-Aktivität mit Strahlungsdaten aufzeichnen.',
        lang: 'Die App folgt der Sprache der Uhr. Einstellungen, die du auf der Uhr änderst, bleiben auf der Uhr.',
    },
    connect: {
        title: 'RadiaCode verbinden',
        warning: 'Ein RadiaCode kann nur mit einem Gerät gleichzeitig verbunden sein. Das Handy zählt mit. Ist die offizielle RadiaCode-Handy-App (oder ein anderer Bluetooth-Client) verbunden, kann die Uhr das Messgerät nicht nutzen. Trenne diese App zuerst und versuche es dann von der Uhr.',
        warningExtra: 'Dasselbe gilt für eine andere Uhr, einen Computer oder ein Tablet. Der RadiaCode darf nirgendwo sonst verbunden sein, bevor du ihn hier verbindest.',
        stepsTitle: 'So koppelst du',
        step1: 'Schalte den RadiaCode ein und halte ihn nah an der Uhr (ein paar Meter reichen).',
        step2: 'Öffne Radia Code App auf der Uhr. Sie sucht sofort und zeigt „Suche…“.',
        step3: 'Erscheint dein Gerät in der Liste, wähle es mit HOCH / RUNTER und drücke START oder SELECT. Auf einem Touchscreen kannst du den Namen antippen.',
        step4: 'Die Uhr bleibt bei „Verbinde…“, bis der erste Live-Wert kommt. Das kann einige Sekunden dauern, besonders auf Instinct-Uhren. Drücke START nicht, solange noch verbunden wird.',
        step5: 'Nach erfolgreicher Verbindung wird der Gerätename gespeichert. Beim nächsten Öffnen verbindet die App automatisch erneut.',
        forget: 'Für ein anderes Messgerät: Menü öffnen und „Gerät vergessen“ wählen. Damit ist Auto-Connect aus. „Neu scannen“ startet eine neue Suche.',
        notFound: 'Erscheint das Gerät nicht oder schlägt Verbinden fehl, hält es fast immer noch die Handy-App oder ein anderer Bluetooth-Client. Dort trennen, kurz warten, dann neu scannen.',
    },
    search: {
        title: 'Suchbildschirm',
        titleLine: 'In der Titelleiste steht „Radia Code“.',
        list: 'Darunter die Liste der per Bluetooth gefundenen RadiaCode-Geräte in der Nähe.',
        instinct: 'Auf Instinct (MIP) sitzen die Tastenhinweise am Gehäuse: Menü Mitte links, Verbinden an der Start-Taste, Zurück unten rechts.',
        empty: 'Während der Suche siehst du „Suche…“. Wird nichts gefunden, neu scannen im Menü.',
    },
    measure: {
        title: 'Messbildschirm',
        intro: 'Sobald der erste Messwert da ist, ersetzt die Live-Ansicht die Verbindungskarte.',
        dose: 'Großer Dosisleistungswert mit der gewählten Einheit (Sievert oder Röntgen, Auto / µSv/h / nSv/h).',
        chart: 'Impulschart der letzten Zählrate. Die Füllung liegt am lokalen Hintergrund, damit ein Anstieg gut sichtbar ist.',
        counts: 'Zählrate als CPS (Impulse pro Sekunde) oder CPM (Impulse pro Minute).',
        footer: 'Fußzeile: RadiaCode-Temperatur und der erkannte lokale Hintergrund.',
        time: 'Die aktuelle Uhrzeit steht im Kopf (auf Instinct im Sub-Display / Pocket).',
        ring: 'Auf runden Farbdisplays zeigt ein Level-Ring (oder ein kleiner Balken bei anderen Formen), wie die aktuelle Rate zum Hintergrund steht.',
        colour: 'Chart- und Wertfarbe folgen der Rate relativ zum lokalen Hintergrund. „Strahlung automatisch erkennen“ wertet etwa das Doppelte des Hintergrunds als erhöht und kann alarmieren.',
        rec: 'Während einer Aktivität zeigt der Kopf REC plus Laufzeit statt der Uhr, die Fußzeile die aufgelaufene Gesamtdosis.',
    },
    controls: {
        title: 'Tasten (verbunden)',
        menu: 'Einstellungen öffnen (Einheiten, GPS, Display, Alarme). Auf dem Suchbildschirm öffnet dieselbe Taste das Scan-Menü.',
        start: 'Optionale Aktivität starten oder stoppen (START/SELECT oder Tippen auf Start/Stop oben rechts). Ein Tipp auf den Rest des Messscreens macht nichts. Während des Verbindens gesperrt.',
        back: 'App verlassen. Läuft eine Aufzeichnung, erscheint zuerst das Stop-Menü (Speichern / Verwerfen / Fortsetzen).',
        select: 'Auf dem Suchbildschirm: das markierte Gerät verbinden.',
    },
    menu: {
        title: 'Menüpunkte',
        scanTitle: 'Suche- / Scan-Menü',
        scanIntro: 'Verfügbar, solange du nicht verbunden bist. Titel ist der App-Name.',
        forget: 'Gerät vergessen — Auto-Connect aus und gespeichertes Gerät löschen (nur wenn eines gespeichert war).',
        rescan: 'Neu scannen — neue Bluetooth-Suche starten.',
        connectedTitle: 'Menü im verbundenen Zustand',
        connectedIntro: 'Dieselben Einstellungen wie unten, plus Trennen, um das aktuelle Gerät zu lösen und zur Liste zurückzukehren.',
        settingsTitle: 'Einstellungen (beide Menüs)',
        dose: 'Dosis-Einheit — Sievert (Sv) oder Röntgen (R).',
        rate: 'Rate-Einheit — Auto, µSv/h oder nSv/h.',
        count: 'Zähleinheit — CPS oder CPM.',
        gps: 'GPS-Position während Aktivität speichern — an: die Aktivität enthält eine GPS-Spur. Aus: die Session wird als Indoor / Training ohne GPS gespeichert.',
        keep: 'Display aktiv halten — Display / Beleuchtung bleibt an, damit du die Rate ohne Aufwecken siehst.',
        auto: 'Strahlung automatisch erkennen — Hinweis, wenn die Rate klar über dem lokalen Hintergrund liegt (etwa 2×).',
        threshold: 'Schwellen-Alarm — absoluter Dosisleistungs-Alarm: Aus, 0,50, 1,00, 2,00, 5,00 oder 10,0 µSv/h.',
    },
    activity: {
        title: 'Optionale Aktivitätsaufzeichnung',
        intro: 'Für die Live-Anzeige musst du nichts aufzeichnen. Die Aufzeichnung ist optional und erzeugt eine normale Garmin-Aktivität in Garmin Connect.',
        start: 'Auf dem Messbildschirm START drücken (oder auf einem Touchscreen die Ecke Start/Aktivität antippen). „Aktivität starten?“ bestätigen. Der Kopf wechselt zu REC und einer laufenden Zeit.',
        gps: 'Ist „GPS-Position während Aktivität speichern“ an, sucht die Uhr einen GPS-Fix. Du siehst „GPS…“, bis eine Position da ist, dann „GPS“.',
        live: 'Während der Aufzeichnung zeigt die Fußzeile die aufgelaufene Gesamtdosis dieser Session.',
        stopTitle: 'Stop-Menü',
        save: 'Session beenden und die FIT-Datei auf der Uhr speichern (danach Sync nach Garmin Connect).',
        discard: 'Beenden ohne Speichern.',
        continue: 'Menü schließen und weiter aufzeichnen.',
        fitTitle: 'Was gespeichert wird',
        fitCpm: 'CPM — Zählrate über die Zeit (Chart in Garmin Connect).',
        fitRate: 'Dosisleistung in µSv/h über die Zeit (Chart).',
        fitTotal: 'Gesamtdosis in µSv für die ganze Session (Aktivitätszusammenfassung).',
        fitLap: 'Runden-Dosis in µSv (pro Runde; Auto-Lap der Uhr, z. B. 1 km, wenn aktiv).',
        fitGps: 'GPS-Spur, nur wenn die GPS-Einstellung beim Start an war.',
        close: 'Schließt du die App während einer laufenden Session, speichert die App die Aktivität, damit nichts verloren geht.',
    },
    instinct: {
        title: 'Hinweise zu Instinct (MIP)',
        buttons: 'Kein Touchscreen. Nutze die beschrifteten Tastenhinweise: Menü, Verbinden / Start, Zurück.',
        connect: 'Verbinden kann länger dauern als auf AMOLED-Uhren. Bleib auf der Verbindungskarte, bis der erste Wert erscheint; die Live-Ansicht kommt nicht früher.',
        chart: 'Der Impulschart ist für das Monochrom-Display invertiert, damit die Kurve lesbar bleibt.',
        time: 'Uhrzeit (und GPS / REC während einer Aktivität) nutzt das kleine Sub-Display.',
    },
    supportBefore: 'Fragen oder Probleme? Bitte nutze das',
    supportLink: 'Kontaktformular',
    supportAfter: '.',
};
export const radiacodeFr = {
    intro: {
        title: 'Qu’est-ce que Radia Code App ?',
        text: 'Radia Code App est une application Garmin Connect IQ qui parle directement à un radiamètre RadiaCode en Bluetooth Low Energy. L’affichage en direct n’a besoin ni de téléphone ni d’internet. Vous voyez le débit de dose, le taux de comptage, un graphique d’impulsions par rapport au fond local, la température de l’appareil, et vous pouvez éventuellement enregistrer une activité Garmin avec les données de rayonnement.',
        lang: 'L’application suit la langue de la montre. Les réglages faits sur la montre restent sur la montre.',
    },
    connect: {
        title: 'Connecter votre RadiaCode',
        warning: 'Un RadiaCode ne peut être connecté qu’à un seul appareil à la fois. Le téléphone compte. Si l’application officielle RadiaCode (ou un autre client Bluetooth) est connectée, la montre ne peut pas utiliser le capteur. Fermez ou déconnectez d’abord cette application, puis réessayez depuis la montre.',
        warningExtra: 'Il en va de même pour une autre montre, un ordinateur ou une tablette. Le RadiaCode ne doit être associé nulle part ailleurs avant de le connecter ici.',
        stepsTitle: 'Comment apparier',
        step1: 'Allumez le RadiaCode et gardez-le près de la montre (quelques mètres suffisent).',
        step2: 'Ouvrez Radia Code App sur la montre. Elle lance immédiatement le scan et affiche « Recherche… ».',
        step3: 'Quand votre appareil apparaît, sélectionnez-le avec HAUT / BAS et appuyez sur START ou SELECT. Sur un écran tactile, vous pouvez toucher le nom.',
        step4: 'La montre reste sur « Connexion… » jusqu’à la première mesure. Cela peut prendre plusieurs secondes, surtout sur Instinct. N’appuyez pas sur START pendant la connexion.',
        step5: 'Après une connexion réussie, le nom est enregistré. Au prochain lancement, l’application se reconnecte automatiquement.',
        forget: 'Pour un autre capteur, ouvrez le menu et choisissez Oublier l’appareil. Cela désactive la connexion auto. Rechercher relance un scan.',
        notFound: 'Si l’appareil n’apparaît pas ou si la connexion échoue, c’est presque toujours l’application téléphone ou un autre client Bluetooth qui le retient. Déconnectez-y, attendez un instant, puis relancez le scan.',
    },
    search: {
        title: 'Écran de recherche',
        titleLine: 'La barre de titre affiche « Radia Code ».',
        list: 'En dessous, la liste des RadiaCode proches trouvés en Bluetooth.',
        instinct: 'Sur Instinct (MIP), les libellés de boutons sont sur le boîtier : Menu au milieu à gauche, Connecter sur START, Retour en bas à droite.',
        empty: 'Pendant le scan : « Recherche… ». Si rien n’est trouvé, relancez depuis le menu.',
    },
    measure: {
        title: 'Écran de mesure',
        intro: 'Dès le premier échantillon, la vue en direct remplace la carte de connexion.',
        dose: 'Grande valeur de débit de dose avec l’unité choisie (Sievert ou Roentgen, auto / µSv/h / nSv/h).',
        chart: 'Graphique d’impulsions du taux de comptage récent. Le remplissage se cale sur le fond local pour qu’une hausse soit visible.',
        counts: 'Taux de comptage en CPS (coups par seconde) ou CPM (coups par minute).',
        footer: 'Pied : température du RadiaCode et fond local détecté.',
        time: 'L’heure actuelle est en en-tête (sur Instinct, dans le sous-écran).',
        ring: 'Sur les écrans ronds couleur, un anneau (ou une petite barre sur d’autres formes) compare le débit actuel au fond.',
        colour: 'La couleur du graphique et de la valeur suit le débit par rapport au fond. La détection auto considère environ 2× le fond comme élevé et peut alerter.',
        rec: 'Pendant un enregistrement, l’en-tête affiche REC et le temps écoulé au lieu de l’heure ; le pied affiche la dose totale accumulée.',
    },
    controls: {
        title: 'Boutons (connecté)',
        menu: 'Ouvre les réglages (unités, GPS, écran, alarmes). Sur l’écran de recherche, le même bouton ouvre le menu de scan.',
        start: 'Démarre ou arrête l’activité optionnelle (START/SELECT, ou appui sur Start/Stop en haut à droite). Un appui ailleurs sur l’écran de mesure ne fait rien. Bloqué pendant la connexion.',
        back: 'Quitte l’application. Si un enregistrement tourne, le menu d’arrêt s’affiche d’abord (Enregistrer / Ignorer / Continuer).',
        select: 'Sur l’écran de recherche : connecter l’appareil sélectionné.',
    },
    menu: {
        title: 'Éléments de menu',
        scanTitle: 'Menu recherche / scan',
        scanIntro: 'Disponible tant que vous n’êtes pas connecté. Le titre est le nom de l’application.',
        forget: 'Oublier l’appareil — désactive la connexion auto et efface le capteur mémorisé (s’il y en a un).',
        rescan: 'Rechercher — relance un scan Bluetooth.',
        connectedTitle: 'Menu connecté',
        connectedIntro: 'Mêmes réglages qu’ci-dessous, plus Déconnecter pour quitter le capteur actuel et revenir à la liste.',
        settingsTitle: 'Réglages (les deux menus)',
        dose: 'Unité de dose — Sievert (Sv) ou Roentgen (R).',
        rate: 'Unité de débit — Auto, µSv/h ou nSv/h.',
        count: 'Unité de comptage — CPS ou CPM.',
        gps: 'Enregistrer la position GPS pendant l’activité — activé : trace GPS. Désactivé : session indoor / entraînement sans GPS.',
        keep: 'Garder l’écran actif — écran / rétroéclairage allumé pour lire le débit sans réveiller la montre.',
        auto: 'Détection auto du rayonnement — alerte si le débit est clairement au-dessus du fond (environ 2×).',
        threshold: 'Alarme seuil — alarme absolue de débit : Off, 0,50, 1,00, 2,00, 5,00 ou 10,0 µSv/h.',
    },
    activity: {
        title: 'Enregistrement d’activité (optionnel)',
        intro: 'Vous n’êtes pas obligé d’enregistrer pour utiliser l’affichage en direct. L’enregistrement est optionnel et crée une activité Garmin normale dans Garmin Connect.',
        start: 'Sur l’écran de mesure, appuyez sur START (ou touchez le coin Start/Activité). Confirmez « Démarrer l’activité ? ». L’en-tête passe à REC et un chronomètre.',
        gps: 'Si « Enregistrer la position GPS » est activé, la montre cherche un fix. Vous pouvez voir « GPS… » puis « GPS ».',
        live: 'Pendant l’enregistrement, le pied affiche la dose totale accumulée de la session.',
        stopTitle: 'Menu d’arrêt',
        save: 'Termine la session et écrit le fichier FIT sur la montre (puis sync vers Garmin Connect).',
        discard: 'Termine sans enregistrer.',
        continue: 'Ferme le menu et continue l’enregistrement.',
        fitTitle: 'Ce qui est stocké',
        fitCpm: 'CPM — taux de comptage dans le temps (graphique dans Garmin Connect).',
        fitRate: 'Débit de dose en µSv/h dans le temps (graphique).',
        fitTotal: 'Dose totale en µSv pour toute la session (résumé).',
        fitLap: 'Dose de tour en µSv (par tour ; auto-tour de la montre, p. ex. 1 km, si activé).',
        fitGps: 'Trace GPS uniquement si le réglage GPS était activé au départ.',
        close: 'Si vous quittez l’application pendant un enregistrement, elle enregistre l’activité pour ne rien perdre.',
    },
    instinct: {
        title: 'Notes Instinct (MIP)',
        buttons: 'Pas d’écran tactile. Utilisez les libellés : Menu, Connecter / Start, Retour.',
        connect: 'La connexion peut être plus longue que sur AMOLED. Restez sur la carte de connexion jusqu’à la première valeur.',
        chart: 'Le graphique d’impulsions est inversé sur l’écran monochrome pour rester lisible.',
        time: 'L’heure (et GPS / REC pendant une activité) utilise le petit sous-écran.',
    },
    supportBefore: 'Questions ou problèmes ? Utilisez le',
    supportLink: 'formulaire de contact',
    supportAfter: '.',
};
export const radiacodeEs = {
    intro: {
        title: '¿Qué es Radia Code App?',
        text: 'Radia Code App es una app Garmin Connect IQ que habla por Bluetooth Low Energy directamente con un medidor RadiaCode. Las lecturas en vivo no necesitan teléfono ni internet. Ves la tasa de dosis, la tasa de cuentas, un gráfico de impulsos frente al fondo local, la temperatura del aparato y, si quieres, puedes grabar una actividad Garmin con datos de radiación.',
        lang: 'La app sigue el idioma del reloj. Los ajustes que cambias en el reloj se quedan en el reloj.',
    },
    connect: {
        title: 'Conectar tu RadiaCode',
        warning: 'Un RadiaCode solo puede estar conectado a un dispositivo a la vez. El teléfono cuenta. Si la app oficial de RadiaCode (u otro cliente Bluetooth) está conectada, el reloj no puede usar el medidor. Ciérrala o desconéctala primero e inténtalo desde el reloj.',
        warningExtra: 'Lo mismo vale para otro reloj, un ordenador o una tableta. El RadiaCode no debe estar emparejado en ningún otro sitio antes de conectarlo aquí.',
        stepsTitle: 'Cómo emparejar',
        step1: 'Enciende el RadiaCode y tenlo cerca del reloj (unos metros bastan).',
        step2: 'Abre Radia Code App en el reloj. Empieza a buscar al momento y muestra «Buscando…».',
        step3: 'Cuando aparezca tu medidor, selecciónalo con ARRIBA / ABAJO y pulsa START o SELECT. En una pantalla táctil puedes tocar el nombre.',
        step4: 'El reloj se queda en «Conectando…» hasta la primera medida. Puede tardar varios segundos, sobre todo en Instinct. No pulses START mientras conecta.',
        step5: 'Tras una conexión correcta se guarda el nombre. La próxima vez la app reconecta sola.',
        forget: 'Para otro medidor, abre el menú y elige Olvidar dispositivo. Eso desactiva la conexión automática. Volver a buscar inicia un nuevo escaneo.',
        notFound: 'Si el medidor no aparece o falla la conexión, casi siempre lo tiene aún la app del teléfono u otro cliente Bluetooth. Desconecta allí, espera un momento y vuelve a buscar.',
    },
    search: {
        title: 'Pantalla de búsqueda',
        titleLine: 'La barra de título muestra «Radia Code».',
        list: 'Debajo, la lista de RadiaCode cercanos encontrados por Bluetooth.',
        instinct: 'En Instinct (MIP), las pistas de botones están en el bisel: Menú a media izquierda, Conectar en START, Atrás abajo a la derecha.',
        empty: 'Durante el escaneo ves «Buscando…». Si no hay nada, vuelve a buscar desde el menú.',
    },
    measure: {
        title: 'Pantalla de medición',
        intro: 'Cuando llega la primera muestra, la vista en vivo sustituye la tarjeta de conexión.',
        dose: 'Valor grande de tasa de dosis con la unidad elegida (Sievert o Roentgen, auto / µSv/h / nSv/h).',
        chart: 'Gráfico de impulsos de la tasa de cuentas reciente. El relleno se dibuja contra el fondo local para ver un aumento.',
        counts: 'Tasa de cuentas como CPS (cuentas por segundo) o CPM (cuentas por minuto).',
        footer: 'Pie: temperatura del RadiaCode y el fondo local detectado.',
        time: 'La hora actual está en la cabecera (en Instinct, en la subpantalla).',
        ring: 'En pantallas redondas a color, un anillo (o una barra pequeña en otras formas) compara la tasa actual con el fondo.',
        colour: 'El color del gráfico y del valor sigue la tasa respecto al fondo. La detección automática trata unos 2× el fondo como elevado y puede alertar.',
        rec: 'Durante una actividad, la cabecera muestra REC y el tiempo transcurrido en vez del reloj; el pie, la dosis total acumulada.',
    },
    controls: {
        title: 'Botones (conectado)',
        menu: 'Abre ajustes (unidades, GPS, pantalla, alarmas). En la pantalla de búsqueda, el mismo botón abre el menú de escaneo.',
        start: 'Inicia o detiene la actividad opcional (START/SELECT, o toca Start/Stop arriba a la derecha). Un toque en el resto de la pantalla de medición no hace nada. Bloqueado mientras conecta.',
        back: 'Salir de la app. Si hay una grabación, primero aparece el menú de parada (Guardar / Descartar / Continuar).',
        select: 'En la pantalla de búsqueda: conectar el dispositivo marcado.',
    },
    menu: {
        title: 'Elementos del menú',
        scanTitle: 'Menú de búsqueda / escaneo',
        scanIntro: 'Disponible mientras no estás conectado. El título es el nombre de la app.',
        forget: 'Olvidar dispositivo — desactiva la conexión automática y borra el medidor guardado (si había uno).',
        rescan: 'Volver a buscar — inicia un nuevo escaneo Bluetooth.',
        connectedTitle: 'Menú conectado',
        connectedIntro: 'Los mismos ajustes que abajo, más Desconectar para soltar el medidor actual y volver a la lista.',
        settingsTitle: 'Ajustes (ambos menús)',
        dose: 'Unidad de dosis — Sievert (Sv) o Roentgen (R).',
        rate: 'Unidad de tasa — Auto, µSv/h o nSv/h.',
        count: 'Unidad de cuentas — CPS o CPM.',
        gps: 'Registrar posición GPS durante la actividad — activado: incluye pista GPS. Desactivado: sesión indoor / entrenamiento sin GPS.',
        keep: 'Mantener pantalla activa — pantalla / luz encendida para ver la tasa sin despertar el reloj.',
        auto: 'Detección automática de radiación — aviso si la tasa está claramente por encima del fondo (unos 2×).',
        threshold: 'Alarma de umbral — alarma absoluta de tasa: Off, 0,50, 1,00, 2,00, 5,00 o 10,0 µSv/h.',
    },
    activity: {
        title: 'Grabación de actividad (opcional)',
        intro: 'No hace falta grabar nada para usar la vista en vivo. La grabación es opcional y crea una actividad Garmin normal en Garmin Connect.',
        start: 'En la pantalla de medición pulsa START (o toca la esquina Start/Actividad). Confirma «¿Iniciar actividad?». La cabecera pasa a REC y un cronómetro.',
        gps: 'Si «Registrar posición GPS» está activado, el reloj busca un fix. Puedes ver «GPS…» y luego «GPS».',
        live: 'Durante la grabación, el pie muestra la dosis total acumulada de la sesión.',
        stopTitle: 'Menú de parada',
        save: 'Termina la sesión y escribe el FIT en el reloj (luego se sincroniza con Garmin Connect).',
        discard: 'Terminar sin guardar.',
        continue: 'Cerrar el menú y seguir grabando.',
        fitTitle: 'Qué se guarda',
        fitCpm: 'CPM — tasa de cuentas en el tiempo (gráfico en Garmin Connect).',
        fitRate: 'Tasa de dosis en µSv/h en el tiempo (gráfico).',
        fitTotal: 'Dosis total en µSv de toda la sesión (resumen).',
        fitLap: 'Dosis de vuelta en µSv (por vuelta; auto-vuelta del reloj, p. ej. 1 km, si está activa).',
        fitGps: 'Pista GPS solo si el ajuste GPS estaba activado al empezar.',
        close: 'Si cierras la app con una sesión en marcha, la app guarda la actividad para no perder los datos.',
    },
    instinct: {
        title: 'Notas Instinct (MIP)',
        buttons: 'No hay pantalla táctil. Usa las pistas: Menú, Conectar / Start, Atrás.',
        connect: 'Conectar puede tardar más que en AMOLED. Quédate en la tarjeta de conexión hasta el primer valor.',
        chart: 'El gráfico de impulsos se dibuja invertido en el display monocromo para que se lea bien.',
        time: 'La hora (y GPS / REC en una actividad) usa la subpantalla pequeña.',
    },
    supportBefore: '¿Preguntas o problemas? Usa el',
    supportLink: 'formulario de contacto',
    supportAfter: '.',
};
export const radiacodeIt = {
    intro: {
        title: 'Che cos’è Radia Code App?',
        text: 'Radia Code App è un’app Garmin Connect IQ che parla in Bluetooth Low Energy direttamente con un misuratore RadiaCode. Le letture live non richiedono telefono né internet. Vedi il rateo di dose, il rateo di conteggio, un grafico a impulsi rispetto al fondo locale, la temperatura del dispositivo e, se vuoi, puoi registrare un’attività Garmin con i dati di radiazione.',
        lang: 'L’app segue la lingua dell’orologio. Le impostazioni cambiate sull’orologio restano sull’orologio.',
    },
    connect: {
        title: 'Collega il RadiaCode',
        warning: 'Un RadiaCode può essere collegato a un solo dispositivo alla volta. Il telefono conta. Se l’app ufficiale RadiaCode (o un altro client Bluetooth) è collegata, l’orologio non può usare il misuratore. Chiudi o scollega prima quell’app, poi riprova dall’orologio.',
        warningExtra: 'Lo stesso vale per un altro orologio, un computer o un tablet. Il RadiaCode non deve essere associato altrove prima di collegarlo qui.',
        stepsTitle: 'Come accoppiare',
        step1: 'Accendi il RadiaCode e tienilo vicino all’orologio (bastano pochi metri).',
        step2: 'Apri Radia Code App sull’orologio. Parte subito la scansione e mostra «Ricerca…».',
        step3: 'Quando il misuratore compare, evidenzialo con SU / GIÙ e premi START o SELECT. Su un touchscreen puoi toccare il nome.',
        step4: 'L’orologio resta su «Connessione…» fino alla prima misura. Possono volerci diversi secondi, soprattutto su Instinct. Non premere START mentre è in connessione.',
        step5: 'Dopo una connessione riuscita il nome viene salvato. Al prossimo avvio l’app si riconnette da sola.',
        forget: 'Per un altro misuratore, apri il menu e scegli Dimentica dispositivo. Disattiva la connessione automatica. Nuova scansione avvia una nuova ricerca.',
        notFound: 'Se il misuratore non compare o la connessione fallisce, di solito è ancora tenuto dall’app del telefono o da un altro client Bluetooth. Scollega lì, attendi un attimo e scansiona di nuovo.',
    },
    search: {
        title: 'Schermata di ricerca',
        titleLine: 'La barra del titolo mostra «Radia Code».',
        list: 'Sotto, l’elenco dei RadiaCode vicini trovati via Bluetooth.',
        instinct: 'Su Instinct (MIP) i suggerimenti dei tasti sono sul bordo: Menu a metà sinistra, Connetti su START, Indietro in basso a destra.',
        empty: 'Durante la scansione vedi «Ricerca…». Se non trovi nulla, nuova scansione dal menu.',
    },
    measure: {
        title: 'Schermata di misura',
        intro: 'Al primo campione, la vista live sostituisce la scheda di connessione.',
        dose: 'Grande valore di rateo di dose con l’unità scelta (Sievert o Roentgen, auto / µSv/h / nSv/h).',
        chart: 'Grafico a impulsi del rateo di conteggio recente. Il riempimento è rispetto al fondo locale, così un rialzo si vede bene.',
        counts: 'Rateo di conteggio come CPS (conteggi al secondo) o CPM (conteggi al minuto).',
        footer: 'Piè: temperatura del RadiaCode e fondo locale rilevato.',
        time: 'L’ora attuale è in intestazione (su Instinct, nel sotto-display).',
        ring: 'Sugli schermi tondi a colori un anello (o una piccola barra su altre forme) confronta il rateo attuale con il fondo.',
        colour: 'Il colore di grafico e valore segue il rateo rispetto al fondo. Il rilevamento automatico tratta circa 2× il fondo come elevato e può allertare.',
        rec: 'Durante un’attività l’intestazione mostra REC e il tempo trascorso invece dell’orologio; il piè la dose totale accumulata.',
    },
    controls: {
        title: 'Tasti (collegato)',
        menu: 'Apre le impostazioni (unità, GPS, schermo, allarmi). Nella schermata di ricerca lo stesso tasto apre il menu di scansione.',
        start: 'Avvia o ferma l’attività opzionale (START/SELECT, o tocca Start/Stop in alto a destra). Un tocco altrove sulla schermata di misura non fa nulla. Bloccato in connessione.',
        back: 'Esce dall’app. Se è in corso una registrazione, prima compare il menu di stop (Salva / Scarta / Continua).',
        select: 'Nella schermata di ricerca: collega il dispositivo evidenziato.',
    },
    menu: {
        title: 'Voci di menu',
        scanTitle: 'Menu ricerca / scansione',
        scanIntro: 'Disponibile se non sei collegato. Il titolo è il nome dell’app.',
        forget: 'Dimentica dispositivo — disattiva la connessione automatica e cancella il misuratore salvato (se c’era).',
        rescan: 'Nuova scansione — avvia una nuova ricerca Bluetooth.',
        connectedTitle: 'Menu collegato',
        connectedIntro: 'Le stesse impostazioni sotto, più Disconnetti per lasciare il misuratore attuale e tornare all’elenco.',
        settingsTitle: 'Impostazioni (entrambi i menu)',
        dose: 'Unità di dose — Sievert (Sv) o Roentgen (R).',
        rate: 'Unità di rateo — Auto, µSv/h o nSv/h.',
        count: 'Unità di conteggio — CPS o CPM.',
        gps: 'Registra posizione GPS durante l’attività — on: traccia GPS. Off: sessione indoor / allenamento senza GPS.',
        keep: 'Mantieni schermo attivo — display / retroilluminazione accesi per leggere il rateo senza svegliare l’orologio.',
        auto: 'Rilevamento automatico radiazione — avviso se il rateo è chiaramente sopra il fondo (circa 2×).',
        threshold: 'Allarme soglia — allarme assoluto di rateo: Off, 0,50, 1,00, 2,00, 5,00 o 10,0 µSv/h.',
    },
    activity: {
        title: 'Registrazione attività (opzionale)',
        intro: 'Non devi registrare nulla per usare la vista live. La registrazione è opzionale e crea un’attività Garmin normale in Garmin Connect.',
        start: 'Nella schermata di misura premi START (o tocca l’angolo Start/Attività). Conferma «Avviare attività?». L’intestazione passa a REC e un timer.',
        gps: 'Se «Registra posizione GPS» è attivo, l’orologio cerca un fix. Puoi vedere «GPS…» e poi «GPS».',
        live: 'Durante la registrazione il piè mostra la dose totale accumulata della sessione.',
        stopTitle: 'Menu di stop',
        save: 'Termina la sessione e scrive il FIT sull’orologio (poi sync su Garmin Connect).',
        discard: 'Termina senza salvare.',
        continue: 'Chiude il menu e continua a registrare.',
        fitTitle: 'Cosa viene salvato',
        fitCpm: 'CPM — rateo di conteggio nel tempo (grafico in Garmin Connect).',
        fitRate: 'Rateo di dose in µSv/h nel tempo (grafico).',
        fitTotal: 'Dose totale in µSv per tutta la sessione (riepilogo).',
        fitLap: 'Dose giro in µSv (per giro; auto-giro dell’orologio, es. 1 km, se attivo).',
        fitGps: 'Traccia GPS solo se l’impostazione GPS era attiva all’avvio.',
        close: 'Se chiudi l’app durante una sessione, l’app salva l’attività per non perdere i dati.',
    },
    instinct: {
        title: 'Note Instinct (MIP)',
        buttons: 'Nessun touchscreen. Usa i suggerimenti: Menu, Connetti / Start, Indietro.',
        connect: 'La connessione può essere più lenta che su AMOLED. Resta sulla scheda di connessione fino al primo valore.',
        chart: 'Il grafico a impulsi è invertito sul display monocromatico per restare leggibile.',
        time: 'L’ora (e GPS / REC in attività) usa il piccolo sotto-display.',
    },
    supportBefore: 'Domande o problemi? Usa il',
    supportLink: 'modulo di contatto',
    supportAfter: '.',
};
export const radiacodePt = {
    intro: {
        title: 'O que é a Radia Code App?',
        text: 'A Radia Code App é uma app Garmin Connect IQ que fala por Bluetooth Low Energy diretamente com um medidor RadiaCode. As leituras em direto não precisam de telefone nem de internet. Vê a taxa de dose, a taxa de contagens, um gráfico de impulsos face ao fundo local, a temperatura do aparelho e, se quiser, pode gravar uma atividade Garmin com dados de radiação.',
        lang: 'A app segue o idioma do relógio. As definições alteradas no relógio ficam no relógio.',
    },
    connect: {
        title: 'Ligar o RadiaCode',
        warning: 'Um RadiaCode só pode estar ligado a um dispositivo de cada vez. O telemóvel conta. Se a app oficial RadiaCode (ou outro cliente Bluetooth) estiver ligada, o relógio não consegue usar o medidor. Feche ou desligue essa app primeiro e tente de novo no relógio.',
        warningExtra: 'O mesmo vale para outro relógio, um computador ou um tablet. O RadiaCode não deve estar emparelhado noutro sítio antes de o ligar aqui.',
        stepsTitle: 'Como emparelhar',
        step1: 'Ligue o RadiaCode e mantenha-o perto do relógio (uns metros chegam).',
        step2: 'Abra a Radia Code App no relógio. Começa a procurar de imediato e mostra «A procurar…».',
        step3: 'Quando o medidor aparecer, selecione-o com CIMA / BAIXO e prima START ou SELECT. Num ecrã tátil pode tocar no nome.',
        step4: 'O relógio fica em «A ligar…» até à primeira medição. Pode demorar vários segundos, sobretudo no Instinct. Não prima START enquanto liga.',
        step5: 'Após uma ligação bem-sucedida o nome é guardado. Da próxima vez a app volta a ligar automaticamente.',
        forget: 'Para outro medidor, abra o menu e escolha Esquecer aparelho. Isso desliga a ligação automática. Nova pesquisa inicia um novo scan.',
        notFound: 'Se o medidor não aparecer ou a ligação falhar, quase sempre ainda está na app do telemóvel ou noutro cliente Bluetooth. Desligue aí, espere um momento e volte a procurar.',
    },
    search: {
        title: 'Ecrã de pesquisa',
        titleLine: 'A barra de título mostra «Radia Code».',
        list: 'Por baixo, a lista de RadiaCode próximos encontrados por Bluetooth.',
        instinct: 'No Instinct (MIP), as dicas de botões estão no bisel: Menu a meio à esquerda, Ligar no START, Voltar em baixo à direita.',
        empty: 'Durante o scan vê «A procurar…». Se não houver nada, nova pesquisa no menu.',
    },
    measure: {
        title: 'Ecrã de medição',
        intro: 'Quando chega a primeira amostra, a vista em direto substitui o cartão de ligação.',
        dose: 'Grande valor de taxa de dose com a unidade escolhida (Sievert ou Roentgen, auto / µSv/h / nSv/h).',
        chart: 'Gráfico de impulsos da taxa de contagens recente. O preenchimento é face ao fundo local para um aumento ser visível.',
        counts: 'Taxa de contagens como CPS (contagens por segundo) ou CPM (contagens por minuto).',
        footer: 'Rodapé: temperatura do RadiaCode e o fundo local detetado.',
        time: 'A hora atual está no cabeçalho (no Instinct, no sub-ecrã).',
        ring: 'Em ecrãs redondos a cores, um anel (ou uma barra pequena noutras formas) compara a taxa atual com o fundo.',
        colour: 'A cor do gráfico e do valor segue a taxa face ao fundo. A deteção automática trata cerca de 2× o fundo como elevado e pode alertar.',
        rec: 'Durante uma atividade, o cabeçalho mostra REC e o tempo decorrido em vez do relógio; o rodapé, a dose total acumulada.',
    },
    controls: {
        title: 'Botões (ligado)',
        menu: 'Abre definições (unidades, GPS, ecrã, alarmes). No ecrã de pesquisa, o mesmo botão abre o menu de scan.',
        start: 'Inicia ou para a atividade opcional (START/SELECT, ou toque em Start/Stop no canto superior direito). Um toque no resto do ecrã de medição não faz nada. Bloqueado durante a ligação.',
        back: 'Sair da app. Se estiver a gravar, aparece primeiro o menu de paragem (Guardar / Descartar / Continuar).',
        select: 'No ecrã de pesquisa: ligar o dispositivo selecionado.',
    },
    menu: {
        title: 'Itens de menu',
        scanTitle: 'Menu de pesquisa / scan',
        scanIntro: 'Disponível enquanto não está ligado. O título é o nome da app.',
        forget: 'Esquecer aparelho — desliga a ligação automática e apaga o medidor guardado (se houver).',
        rescan: 'Nova pesquisa — inicia um novo scan Bluetooth.',
        connectedTitle: 'Menu ligado',
        connectedIntro: 'As mesmas definições abaixo, mais Desligar para largar o medidor atual e voltar à lista.',
        settingsTitle: 'Definições (ambos os menus)',
        dose: 'Unidade de dose — Sievert (Sv) ou Roentgen (R).',
        rate: 'Unidade de taxa — Auto, µSv/h ou nSv/h.',
        count: 'Unidade de contagens — CPS ou CPM.',
        gps: 'Registar posição GPS durante a atividade — ligado: inclui rasto GPS. Desligado: sessão indoor / treino sem GPS.',
        keep: 'Manter ecrã ativo — ecrã / luz acesos para ver a taxa sem acordar o relógio.',
        auto: 'Deteção automática de radiação — alerta se a taxa estiver claramente acima do fundo (cerca de 2×).',
        threshold: 'Alarme de limiar — alarme absoluto de taxa: Off, 0,50, 1,00, 2,00, 5,00 ou 10,0 µSv/h.',
    },
    activity: {
        title: 'Gravação de atividade (opcional)',
        intro: 'Não precisa de gravar nada para usar a vista em direto. A gravação é opcional e cria uma atividade Garmin normal no Garmin Connect.',
        start: 'No ecrã de medição prima START (ou toque no canto Start/Atividade). Confirme «Iniciar atividade?». O cabeçalho passa a REC e um cronómetro.',
        gps: 'Se «Registar posição GPS» estiver ligado, o relógio procura um fix. Pode ver «GPS…» e depois «GPS».',
        live: 'Durante a gravação, o rodapé mostra a dose total acumulada desta sessão.',
        stopTitle: 'Menu de paragem',
        save: 'Termina a sessão e escreve o FIT no relógio (depois sincroniza com o Garmin Connect).',
        discard: 'Terminar sem guardar.',
        continue: 'Fechar o menu e continuar a gravar.',
        fitTitle: 'O que é guardado',
        fitCpm: 'CPM — taxa de contagens ao longo do tempo (gráfico no Garmin Connect).',
        fitRate: 'Taxa de dose em µSv/h ao longo do tempo (gráfico).',
        fitTotal: 'Dose total em µSv de toda a sessão (resumo).',
        fitLap: 'Dose de volta em µSv (por volta; auto-volta do relógio, p. ex. 1 km, se ativa).',
        fitGps: 'Rasto GPS só se a definição GPS estava ligada no início.',
        close: 'Se fechar a app com uma sessão a decorrer, a app guarda a atividade para não perder dados.',
    },
    instinct: {
        title: 'Notas Instinct (MIP)',
        buttons: 'Não há ecrã tátil. Use as dicas: Menu, Ligar / Start, Voltar.',
        connect: 'Ligar pode demorar mais do que em AMOLED. Fique no cartão de ligação até ao primeiro valor.',
        chart: 'O gráfico de impulsos é invertido no ecrã monocromático para se ler bem.',
        time: 'A hora (e GPS / REC numa atividade) usa o sub-ecrã pequeno.',
    },
    supportBefore: 'Questões ou problemas? Use o',
    supportLink: 'formulário de contacto',
    supportAfter: '.',
};
export const radiacodeNl = {
    intro: {
        title: 'Wat is Radia Code App?',
        text: 'Radia Code App is een Garmin Connect IQ-horloge-app die via Bluetooth Low Energy rechtstreeks met een RadiaCode-stralingsmeter praat. Live-waarden hebben geen telefoon of internet nodig. Je ziet dosistempo, telsnelheid, een impulsgrafiek tegen de lokale achtergrond, de temperatuur van het apparaat en je kunt optioneel een Garmin-activiteit met stralingsdata opnemen.',
        lang: 'De app volgt de taal van het horloge. Instellingen die je op het horloge wijzigt, blijven op het horloge.',
    },
    connect: {
        title: 'Je RadiaCode verbinden',
        warning: 'Een RadiaCode kan maar met één apparaat tegelijk verbonden zijn. De telefoon telt mee. Als de officiële RadiaCode-app (of een andere Bluetooth-client) verbonden is, kan het horloge de meter niet gebruiken. Sluit of ontkoppel die app eerst en probeer het daarna vanaf het horloge.',
        warningExtra: 'Hetzelfde geldt voor een ander horloge, een computer of een tablet. De RadiaCode mag nergens anders gekoppeld zijn voordat je hem hier verbindt.',
        stepsTitle: 'Zo koppel je',
        step1: 'Zet de RadiaCode aan en houd hem dicht bij het horloge (een paar meter is genoeg).',
        step2: 'Open Radia Code App op het horloge. Het scannen start meteen en toont „Zoeken…”.',
        step3: 'Als je meter in de lijst staat, selecteer hem met OMHOOG / OMLAAG en druk op START of SELECT. Op een touchscreen kun je de naam tikken.',
        step4: 'Het horloge blijft op „Verbinden…” tot de eerste live-waarde. Dat kan enkele seconden duren, vooral op Instinct. Druk niet op START tijdens het verbinden.',
        step5: 'Na een geslaagde verbinding wordt de naam opgeslagen. De volgende keer verbindt de app automatisch opnieuw.',
        forget: 'Voor een andere meter: open het menu en kies Apparaat vergeten. Dat zet auto-connect uit. Opnieuw scannen start een nieuwe zoektocht.',
        notFound: 'Als de meter niet verschijnt of verbinden mislukt, houdt bijna altijd de telefoon-app of een andere Bluetooth-client hem nog vast. Ontkoppel daar, wacht even en scan opnieuw.',
    },
    search: {
        title: 'Zoekscherm',
        titleLine: 'De titelbalk toont „Radia Code”.',
        list: 'Daaronder de lijst van nabije RadiaCode-apparaten via Bluetooth.',
        instinct: 'Op Instinct (MIP) zitten knophints op de behuizing: Menu midden-links, Verbinden bij START, Terug rechtsonder.',
        empty: 'Tijdens het scannen zie je „Zoeken…”. Niets gevonden? Opnieuw scannen in het menu.',
    },
    measure: {
        title: 'Meetscherm',
        intro: 'Zodra het eerste monster er is, vervangt de live-weergave de verbindingskaart.',
        dose: 'Grote dosistempowaarde met de gekozen eenheid (Sievert of Röntgen, auto / µSv/h / nSv/h).',
        chart: 'Impulsgrafiek van de recente telsnelheid. De vulling ligt tegen de lokale achtergrond, zodat een stijging zichtbaar is.',
        counts: 'Telsnelheid als CPS (tellen per seconde) of CPM (tellen per minuut).',
        footer: 'Voetregel: RadiaCode-temperatuur en de gedetecteerde lokale achtergrond.',
        time: 'De huidige tijd staat in de kop (op Instinct in het sub-display).',
        ring: 'Op ronde kleuren-displays toont een levelring (of een kleine balk op andere vormen) hoe het huidige tempo zich tot de achtergrond verhoudt.',
        colour: 'Kleur van grafiek en waarde volgt het tempo t.o.v. de achtergrond. Auto-detectie ziet ongeveer 2× achtergrond als verhoogd en kan alarmeren.',
        rec: 'Tijdens een activiteit toont de kop REC plus looptijd in plaats van de klok; de voetregel de opgelopen totale dosis.',
    },
    controls: {
        title: 'Knoppen (verbonden)',
        menu: 'Opent instellingen (eenheden, GPS, scherm, alarmen). Op het zoekscherm opent dezelfde knop het scanmenu.',
        start: 'Start of stop de optionele activiteit (START/SELECT, of tik Start/Stop rechtsboven). Tikken elders op het meetscherm doet niets. Geblokkeerd tijdens verbinden.',
        back: 'App verlaten. Loopt er een opname, dan eerst het stopmenu (Opslaan / Verwerpen / Doorgaan).',
        select: 'Op het zoekscherm: het gemarkeerde apparaat verbinden.',
    },
    menu: {
        title: 'Menu-items',
        scanTitle: 'Zoek- / scanmenu',
        scanIntro: 'Beschikbaar zolang je niet verbonden bent. De titel is de appnaam.',
        forget: 'Apparaat vergeten — auto-connect uit en opgeslagen meter wissen (als die er was).',
        rescan: 'Opnieuw scannen — nieuwe Bluetooth-zoektocht.',
        connectedTitle: 'Menu verbonden',
        connectedIntro: 'Dezelfde instellingen als hieronder, plus Ontkoppelen om de huidige meter los te laten en terug te gaan naar de lijst.',
        settingsTitle: 'Instellingen (beide menu’s)',
        dose: 'Dosis-eenheid — Sievert (Sv) of Röntgen (R).',
        rate: 'Tempo-eenheid — Auto, µSv/h of nSv/h.',
        count: 'Teleenheid — CPS of CPM.',
        gps: 'GPS-positie tijdens activiteit vastleggen — aan: GPS-spoor. Uit: indoor / training zonder GPS.',
        keep: 'Scherm actief houden — scherm / hintergrondlicht aan om het tempo te zien zonder het horloge te wekken.',
        auto: 'Straling automatisch detecteren — melding als het tempo duidelijk boven de achtergrond ligt (ongeveer 2×).',
        threshold: 'Drempelalarm — absoluut dosistempo-alarm: Uit, 0,50, 1,00, 2,00, 5,00 of 10,0 µSv/h.',
    },
    activity: {
        title: 'Optionele activiteitsopname',
        intro: 'Je hoeft niets op te nemen voor de live-weergave. Opnemen is optioneel en maakt een normale Garmin-activiteit in Garmin Connect.',
        start: 'Op het meetscherm START drukken (of op touch de hoek Start/Activiteit). Bevestig „Activiteit starten?”. De kop wisselt naar REC en een timer.',
        gps: 'Als „GPS-positie tijdens activiteit” aan staat, zoekt het horloge een fix. Je kunt „GPS…” zien en daarna „GPS”.',
        live: 'Tijdens opnemen toont de voetregel de opgelopen totale dosis van deze sessie.',
        stopTitle: 'Stopmenu',
        save: 'Sessie beëindigen en het FIT-bestand op het horloge schrijven (daarna sync naar Garmin Connect).',
        discard: 'Beëindigen zonder op te slaan.',
        continue: 'Menu sluiten en blijven opnemen.',
        fitTitle: 'Wat wordt opgeslagen',
        fitCpm: 'CPM — telsnelheid in de tijd (grafiek in Garmin Connect).',
        fitRate: 'Dosistempo in µSv/h in de tijd (grafiek).',
        fitTotal: 'Totale dosis in µSv voor de hele sessie (samenvatting).',
        fitLap: 'Rondedosis in µSv (per ronde; auto-ronde van het horloge, bijv. 1 km, indien aan).',
        fitGps: 'GPS-spoor alleen als GPS bij de start aan stond.',
        close: 'Sluit je de app tijdens een sessie, dan slaat de app de activiteit op zodat er niets verloren gaat.',
    },
    instinct: {
        title: 'Instinct (MIP) notities',
        buttons: 'Geen touchscreen. Gebruik de hints: Menu, Verbinden / Start, Terug.',
        connect: 'Verbinden kan langer duren dan op AMOLED. Blijf op de verbindingskaart tot de eerste waarde.',
        chart: 'De impulsgrafiek is omgekeerd op het monochrome scherm zodat de golf leesbaar blijft.',
        time: 'Tijd (en GPS / REC tijdens een activiteit) gebruikt het kleine sub-display.',
    },
    supportBefore: 'Vragen of problemen? Gebruik het',
    supportLink: 'contactformulier',
    supportAfter: '.',
};
export const radiacodePl = {
    intro: {
        title: 'Czym jest Radia Code App?',
        text: 'Radia Code App to aplikacja Garmin Connect IQ, która łączy się bezpośrednio z miernikiem RadiaCode przez Bluetooth Low Energy. Podgląd na żywo nie wymaga telefonu ani internetu. Widzisz moc dawki, tempo zliczeń, wykres impulsów względem lokalnego tła, temperaturę urządzenia i opcjonalnie możesz nagrać aktywność Garmin z danymi o promieniowaniu.',
        lang: 'Aplikacja używa języka zegarka. Ustawienia zmienione na zegarku zostają na zegarku.',
    },
    connect: {
        title: 'Połącz RadiaCode',
        warning: 'RadiaCode może być połączony tylko z jednym urządzeniem naraz. Telefon też się liczy. Jeśli oficjalna aplikacja RadiaCode (lub inny klient Bluetooth) jest połączona, zegarek nie użyje miernika. Najpierw ją zamknij lub rozłącz, potem spróbuj na zegarku.',
        warningExtra: 'To samo dotyczy innego zegarka, komputera lub tabletu. RadiaCode nie może być sparowany nigdzie indziej przed połączeniem tutaj.',
        stepsTitle: 'Jak sparować',
        step1: 'Włącz RadiaCode i trzymaj go blisko zegarka (wystarczy kilka metrów).',
        step2: 'Otwórz Radia Code App na zegarku. Od razu skanuje i pokazuje „Szukanie…”.',
        step3: 'Gdy miernik pojawi się na liście, zaznacz go GÓRA / DÓŁ i naciśnij START lub SELECT. Na ekranie dotykowym możesz stuknąć nazwę.',
        step4: 'Zegarek zostaje na „Łączenie…” do pierwszego pomiaru. To może zająć kilka sekund, zwłaszcza na Instinct. Nie naciskaj START podczas łączenia.',
        step5: 'Po udanym połączeniu nazwa jest zapisywana. Następnym razem aplikacja połączy się automatycznie.',
        forget: 'Dla innego miernika otwórz menu i wybierz Zapomnij urządzenie. To wyłącza auto-łączenie. Ponowne skanowanie startuje nowe wyszukiwanie.',
        notFound: 'Jeśli miernik się nie pojawia lub łączenie się nie udaje, prawie zawsze trzyma go jeszcze aplikacja w telefonie lub inny klient Bluetooth. Rozłącz tam, poczekaj chwilę i skanuj ponownie.',
    },
    search: {
        title: 'Ekran wyszukiwania',
        titleLine: 'Pasek tytułu pokazuje „Radia Code”.',
        list: 'Poniżej lista pobliskich urządzeń RadiaCode znalezionych przez Bluetooth.',
        instinct: 'Na Instinct (MIP) podpowiedzi przycisków są na obudowie: Menu pośrodku z lewej, Połącz przy START, Wstecz na dole po prawej.',
        empty: 'Podczas skanowania widać „Szukanie…”. Jeśli nic nie ma, ponowne skanowanie w menu.',
    },
    measure: {
        title: 'Ekran pomiaru',
        intro: 'Gdy dotrze pierwsza próbka, widok na żywo zastępuje kartę łączenia.',
        dose: 'Duża wartość mocy dawki z wybraną jednostką (Siwert lub Rentgen, auto / µSv/h / nSv/h).',
        chart: 'Wykres impulsów ostatniego tempa zliczeń. Wypełnienie jest względem lokalnego tła, żeby wzrost był widoczny.',
        counts: 'Tempo zliczeń jako CPS (zliczenia na sekundę) lub CPM (zliczenia na minutę).',
        footer: 'Stopka: temperatura RadiaCode i wykryte lokalne tło.',
        time: 'Bieżąca godzina jest w nagłówku (na Instinct na sub-wyświetlaczu).',
        ring: 'Na okrągłych kolorowych ekranach pierścień (lub mały pasek na innych kształtach) porównuje bieżące tempo z tłem.',
        colour: 'Kolor wykresu i wartości zależy od tempa względem tła. Autodetekcja traktuje ok. 2× tło jako podwyższone i może alarmować.',
        rec: 'Podczas aktywności nagłówek pokazuje REC i czas zamiast zegara, stopka — skumulowaną dawkę całkowitą.',
    },
    controls: {
        title: 'Przyciski (połączono)',
        menu: 'Otwiera ustawienia (jednostki, GPS, ekran, alarmy). Na ekranie wyszukiwania ten sam przycisk otwiera menu skanowania.',
        start: 'Start lub stop opcjonalnej aktywności (START/SELECT albo stuknij Start/Stop w prawym górnym rogu). Stuknięcie w resztę ekranu pomiaru nic nie robi. Zablokowane podczas łączenia.',
        back: 'Wyjście z aplikacji. Jeśli trwa nagranie, najpierw menu stop (Zapisz / Odrzuć / Kontynuuj).',
        select: 'Na ekranie wyszukiwania: połącz zaznaczone urządzenie.',
    },
    menu: {
        title: 'Pozycje menu',
        scanTitle: 'Menu wyszukiwania / skanowania',
        scanIntro: 'Dostępne, gdy nie jesteś połączony. Tytuł to nazwa aplikacji.',
        forget: 'Zapomnij urządzenie — wyłącza auto-łączenie i czyści zapisany miernik (jeśli był).',
        rescan: 'Skanuj ponownie — nowe wyszukiwanie Bluetooth.',
        connectedTitle: 'Menu połączone',
        connectedIntro: 'Te same ustawienia co poniżej plus Rozłącz, aby odłączyć bieżący miernik i wrócić do listy.',
        settingsTitle: 'Ustawienia (oba menu)',
        dose: 'Jednostka dawki — Siwert (Sv) lub Rentgen (R).',
        rate: 'Jednostka tempa — Auto, µSv/h lub nSv/h.',
        count: 'Jednostka zliczeń — CPS lub CPM.',
        gps: 'Zapisuj pozycję GPS podczas aktywności — włączone: ślad GPS. Wyłączone: sesja indoor / trening bez GPS.',
        keep: 'Utrzymuj ekran aktywny — wyświetlacz / podświetlenie włączone, żeby zobaczyć tempo bez budzenia zegarka.',
        auto: 'Autodetekcja promieniowania — alert, gdy tempo jest wyraźnie powyżej tła (ok. 2×).',
        threshold: 'Alarm progu — bezwzględny alarm mocy dawki: Wył., 0,50, 1,00, 2,00, 5,00 lub 10,0 µSv/h.',
    },
    activity: {
        title: 'Opcjonalne nagrywanie aktywności',
        intro: 'Nie musisz nic nagrywać, żeby korzystać z podglądu. Nagrywanie jest opcjonalne i tworzy zwykłą aktywność Garmin w Garmin Connect.',
        start: 'Na ekranie pomiaru naciśnij START (lub stuknij róg Start/Aktywność). Potwierdź „Rozpocząć aktywność?”. Nagłówek przełącza się na REC i stoper.',
        gps: 'Jeśli „Zapisuj pozycję GPS” jest włączone, zegarek szuka fixu. Możesz zobaczyć „GPS…”, potem „GPS”.',
        live: 'Podczas nagrania stopka pokazuje skumulowaną dawkę całkowitą tej sesji.',
        stopTitle: 'Menu stop',
        save: 'Kończy sesję i zapisuje plik FIT na zegarku (potem sync do Garmin Connect).',
        discard: 'Zakończ bez zapisu.',
        continue: 'Zamknij menu i nagrywaj dalej.',
        fitTitle: 'Co jest zapisywane',
        fitCpm: 'CPM — tempo zliczeń w czasie (wykres w Garmin Connect).',
        fitRate: 'Moc dawki w µSv/h w czasie (wykres).',
        fitTotal: 'Dawka całkowita w µSv dla całej sesji (podsumowanie).',
        fitLap: 'Dawka okrążenia w µSv (na okrążenie; auto-okrążenie zegarka, np. 1 km, jeśli włączone).',
        fitGps: 'Ślad GPS tylko jeśli GPS było włączone na starcie.',
        close: 'Jeśli zamkniesz aplikację podczas sesji, zapisze aktywność, żeby nic nie zginęło.',
    },
    instinct: {
        title: 'Uwagi Instinct (MIP)',
        buttons: 'Brak ekranu dotykowego. Użyj podpowiedzi: Menu, Połącz / Start, Wstecz.',
        connect: 'Łączenie może trwać dłużej niż na AMOLED. Zostań na karcie łączenia do pierwszej wartości.',
        chart: 'Wykres impulsów jest odwrócony na monochromie, żeby fala była czytelna.',
        time: 'Czas (oraz GPS / REC podczas aktywności) używa małego sub-wyświetlacza.',
    },
    supportBefore: 'Pytania lub problemy? Skorzystaj z',
    supportLink: 'formularza kontaktowego',
    supportAfter: '.',
};
export const radiacodeCs = {
    intro: {
        title: 'Co je Radia Code App?',
        text: 'Radia Code App je aplikace Garmin Connect IQ, která mluví přímo s měřičem RadiaCode přes Bluetooth Low Energy. Živé hodnoty nepotřebují telefon ani internet. Vidíte dávkový příkon, četnost impulsů, graf impulsů vůči místnímu pozadí, teplotu zařízení a volitelně můžete nahrát aktivitu Garmin s daty o záření.',
        lang: 'Aplikace následuje jazyk hodinek. Nastavení změněná na hodinkách zůstanou na hodinkách.',
    },
    connect: {
        title: 'Připojit RadiaCode',
        warning: 'RadiaCode může být připojen jen k jednomu zařízení najednou. Telefon se počítá. Pokud je připojená oficiální aplikace RadiaCode (nebo jiný Bluetooth klient), hodinky měřič nepoužijí. Nejdřív ji ukončete nebo odpojte a zkuste to z hodinek.',
        warningExtra: 'Totéž platí pro jiné hodinky, počítač nebo tablet. RadiaCode nesmí být spárovaný jinde, než ho připojíte tady.',
        stepsTitle: 'Jak spárovat',
        step1: 'Zapněte RadiaCode a držte ho blízko hodinek (stačí pár metrů).',
        step2: 'Otevřete Radia Code App na hodinkách. Okamžitě skenuje a ukazuje „Hledání…”.',
        step3: 'Až se měřič objeví v seznamu, vyberte ho NAHORU / DOLŮ a stiskněte START nebo SELECT. Na dotykové obrazovce můžete klepnout na název.',
        step4: 'Hodinky zůstanou na „Připojování…”, dokud nedorazí první živá hodnota. Může to trvat několik sekund, zejména na Instinct. Nestiskávejte START, dokud se připojuje.',
        step5: 'Po úspěšném připojení se název uloží. Příště se aplikace připojí sama.',
        forget: 'Pro jiný měřič otevřete menu a zvolte Zapomenout zařízení. Tím se vypne automatické připojení. Nové skenování spustí nové hledání.',
        notFound: 'Pokud se měřič neobjeví nebo připojení selže, téměř vždy ho ještě drží telefonní aplikace nebo jiný Bluetooth klient. Tam odpojte, chvíli počkejte a skenujte znovu.',
    },
    search: {
        title: 'Obrazovka hledání',
        titleLine: 'Titulek ukazuje „Radia Code”.',
        list: 'Pod ním seznam blízkých zařízení RadiaCode nalezených přes Bluetooth.',
        instinct: 'Na Instinct (MIP) jsou nápovědy tlačítek na pouzdře: Menu vlevo uprostřed, Připojit u START, Zpět vpravo dole.',
        empty: 'Během skenování vidíte „Hledání…”. Nic nenalezeno? Nové skenování v menu.',
    },
    measure: {
        title: 'Obrazovka měření',
        intro: 'Jakmile dorazí první vzorek, živý pohled nahradí kartu připojení.',
        dose: 'Velká hodnota dávkového příkonu ve zvolené jednotce (Sievert nebo Rentgen, auto / µSv/h / nSv/h).',
        chart: 'Graf impulsů nedávné četnosti. Výplň je vůči místnímu pozadí, aby nárůst byl vidět.',
        counts: 'Četnost jako CPS (impulzy za sekundu) nebo CPM (impulzy za minutu).',
        footer: 'Patička: teplota RadiaCode a zjištěné místní pozadí.',
        time: 'Aktuální čas je v záhlaví (na Instinct na sub-displeji).',
        ring: 'Na kulatých barevných displejích prstenec (nebo malý pruh u jiných tvarů) porovnává aktuální příkon s pozadím.',
        colour: 'Barva grafu a hodnoty sleduje příkon vůči pozadí. Automatická detekce bere zhruba 2× pozadí jako zvýšené a může upozornit.',
        rec: 'Během aktivity záhlaví ukazuje REC a uplynulý čas místo hodin, patička nahromaděnou celkovou dávku.',
    },
    controls: {
        title: 'Tlačítka (připojeno)',
        menu: 'Otevře nastavení (jednotky, GPS, displej, alarmy). Na obrazovce hledání stejné tlačítko otevře menu skenování.',
        start: 'Spustí nebo zastaví volitelnou aktivitu (START/SELECT, nebo klepněte na Start/Stop vpravo nahoře). Klepnutí jinam na obrazovku měření nic nedělá. Během připojování zablokováno.',
        back: 'Opustit aplikaci. Pokud běží nahrávání, nejdřív menu zastavení (Uložit / Zahodit / Pokračovat).',
        select: 'Na obrazovce hledání: připojit označené zařízení.',
    },
    menu: {
        title: 'Položky menu',
        scanTitle: 'Menu hledání / skenování',
        scanIntro: 'Dostupné, když nejste připojeni. Titulek je název aplikace.',
        forget: 'Zapomenout zařízení — vypne automatické připojení a smaže uložený měřič (pokud byl).',
        rescan: 'Nové skenování — nové hledání Bluetooth.',
        connectedTitle: 'Menu po připojení',
        connectedIntro: 'Stejná nastavení jako níže plus Odpojit, abyste uvolnili aktuální měřič a vrátili se k seznamu.',
        settingsTitle: 'Nastavení (obě menu)',
        dose: 'Jednotka dávky — Sievert (Sv) nebo Rentgen (R).',
        rate: 'Jednotka příkonu — Auto, µSv/h nebo nSv/h.',
        count: 'Jednotka četnosti — CPS nebo CPM.',
        gps: 'Ukládat GPS během aktivity — zapnuto: GPS stopa. Vypnuto: indoor / trénink bez GPS.',
        keep: 'Nechat displej aktivní — displej / podsvícení zapnuté, abyste viděli příkon bez probuzení hodinek.',
        auto: 'Automatická detekce záření — upozornění, když je příkon zřetelně nad pozadím (asi 2×).',
        threshold: 'Prahový alarm — absolutní alarm příkonu: Vyp, 0,50, 1,00, 2,00, 5,00 nebo 10,0 µSv/h.',
    },
    activity: {
        title: 'Volitelné nahrávání aktivity',
        intro: 'Pro živý pohled nemusíte nic nahrávat. Nahrávání je volitelné a vytvoří běžnou aktivitu Garmin v Garmin Connect.',
        start: 'Na obrazovce měření stiskněte START (nebo klepněte na roh Start/Aktivita). Potvrďte „Spustit aktivitu?”. Záhlaví přepne na REC a časovač.',
        gps: 'Pokud je „Ukládat GPS během aktivity” zapnuto, hodinky hledají fix. Můžete vidět „GPS…” a pak „GPS”.',
        live: 'Během nahrávání patička ukazuje nahromaděnou celkovou dávku této relace.',
        stopTitle: 'Menu zastavení',
        save: 'Ukončí relaci a zapíše FIT na hodinky (poté sync do Garmin Connect).',
        discard: 'Ukončit bez uložení.',
        continue: 'Zavřít menu a nahrávat dál.',
        fitTitle: 'Co se ukládá',
        fitCpm: 'CPM — četnost v čase (graf v Garmin Connect).',
        fitRate: 'Dávkový příkon v µSv/h v čase (graf).',
        fitTotal: 'Celková dávka v µSv za celou relaci (souhrn).',
        fitLap: 'Dávka kola v µSv (na kolo; auto-kolo hodinek, např. 1 km, pokud je zapnuto).',
        fitGps: 'GPS stopa jen pokud bylo GPS při startu zapnuto.',
        close: 'Pokud aplikaci zavřete během relace, uloží aktivitu, aby data nezmizela.',
    },
    instinct: {
        title: 'Poznámky Instinct (MIP)',
        buttons: 'Žádná dotyková obrazovka. Použijte nápovědy: Menu, Připojit / Start, Zpět.',
        connect: 'Připojení může trvat déle než na AMOLED. Zůstaňte na kartě připojení do první hodnoty.',
        chart: 'Graf impulsů je na monochromu invertovaný, aby vlna zůstala čitelná.',
        time: 'Čas (a GPS / REC během aktivity) používá malý sub-displej.',
    },
    supportBefore: 'Otázky nebo problémy? Použijte',
    supportLink: 'kontaktní formulář',
    supportAfter: '.',
};
export const radiacodeRu = {
    intro: {
        title: 'Что такое Radia Code App?',
        text: 'Radia Code App — приложение Garmin Connect IQ, которое напрямую по Bluetooth Low Energy связывается с дозиметром RadiaCode. Для живых показаний не нужны телефон и интернет. Вы видите мощность дозы, скорость счёта, импульсный график относительно локального фона, температуру прибора и при желании можете записать активность Garmin с данными о радиации.',
        lang: 'Приложение следует языку часов. Настройки, изменённые на часах, хранятся на часах.',
    },
    connect: {
        title: 'Подключить RadiaCode',
        warning: 'RadiaCode может быть подключён только к одному устройству одновременно. Телефон тоже считается. Если подключено официальное приложение RadiaCode (или другой Bluetooth-клиент), часы не смогут использовать прибор. Сначала закройте или отключите его, затем повторите с часов.',
        warningExtra: 'То же относится к другим часам, компьютеру или планшету. RadiaCode не должен быть сопряжён где-либо ещё, прежде чем подключать его здесь.',
        stepsTitle: 'Как сопрячь',
        step1: 'Включите RadiaCode и держите его рядом с часами (достаточно нескольких метров).',
        step2: 'Откройте Radia Code App на часах. Сканирование начинается сразу и показывает «Поиск…».',
        step3: 'Когда прибор появится в списке, выберите его ВВЕРХ / ВНИЗ и нажмите START или SELECT. На сенсорном экране можно нажать на имя.',
        step4: 'Часы остаются на «Подключение…», пока не придёт первое живое значение. Это может занять несколько секунд, особенно на Instinct. Не нажимайте START во время подключения.',
        step5: 'После успешного подключения имя сохраняется. В следующий раз приложение подключится само.',
        forget: 'Для другого прибора откройте меню и выберите Забыть устройство. Это отключает автоподключение. Повторное сканирование запускает новый поиск.',
        notFound: 'Если прибор не появляется или подключение не удаётся, его почти всегда ещё держит телефонное приложение или другой Bluetooth-клиент. Отключите там, подождите и сканируйте снова.',
    },
    search: {
        title: 'Экран поиска',
        titleLine: 'В заголовке показано «Radia Code».',
        list: 'Ниже список ближайших устройств RadiaCode, найденных по Bluetooth.',
        instinct: 'На Instinct (MIP) подсказки кнопок на корпусе: Меню слева по центру, Подключить на START, Назад справа внизу.',
        empty: 'Во время сканирования видно «Поиск…». Ничего нет — повторное сканирование в меню.',
    },
    measure: {
        title: 'Экран измерений',
        intro: 'Как только приходит первый отсчёт, живой экран заменяет карточку подключения.',
        dose: 'Крупное значение мощности дозы в выбранных единицах (зиверт или рентген, авто / µSv/h / nSv/h).',
        chart: 'Импульсный график недавней скорости счёта. Заливка относительно локального фона, чтобы рост было видно.',
        counts: 'Скорость счёта как CPS (счёты в секунду) или CPM (счёты в минуту).',
        footer: 'Подвал: температура RadiaCode и обнаруженный локальный фон.',
        time: 'Текущее время в шапке (на Instinct — на субдисплее).',
        ring: 'На круглых цветных экранах кольцо (или небольшая полоса на других формах) сравнивает текущую мощность с фоном.',
        colour: 'Цвет графика и значения следует мощности относительно фона. Автоопределение считает примерно 2× фона повышенным и может подать сигнал.',
        rec: 'Во время активности шапка показывает REC и время вместо часов, подвал — накопленную полную дозу.',
    },
    controls: {
        title: 'Кнопки (подключено)',
        menu: 'Открывает настройки (единицы, GPS, экран, сигналы). На экране поиска та же кнопка открывает меню сканирования.',
        start: 'Старт или стоп необязательной активности (START/SELECT или нажмите Start/Stop справа сверху). Нажатие в другом месте экрана измерений ничего не делает. Заблокировано во время подключения.',
        back: 'Выйти из приложения. Если идёт запись, сначала меню остановки (Сохранить / Отменить / Продолжить).',
        select: 'На экране поиска: подключить выбранное устройство.',
    },
    menu: {
        title: 'Пункты меню',
        scanTitle: 'Меню поиска / сканирования',
        scanIntro: 'Доступно, пока вы не подключены. Заголовок — имя приложения.',
        forget: 'Забыть устройство — отключает автоподключение и удаляет сохранённый прибор (если был).',
        rescan: 'Сканировать снова — новый поиск Bluetooth.',
        connectedTitle: 'Меню в подключенном состоянии',
        connectedIntro: 'Те же настройки, что ниже, плюс Отключить, чтобы отпустить текущий прибор и вернуться к списку.',
        settingsTitle: 'Настройки (оба меню)',
        dose: 'Единица дозы — зиверт (Sv) или рентген (R).',
        rate: 'Единица мощности — Авто, µSv/h или nSv/h.',
        count: 'Единица счёта — CPS или CPM.',
        gps: 'Записывать GPS во время активности — вкл.: трек GPS. Выкл.: сессия indoor / тренировка без GPS.',
        keep: 'Держать экран активным — дисплей / подсветка включены, чтобы видеть мощность, не будя часы.',
        auto: 'Автоопределение излучения — сигнал, если мощность явно выше фона (около 2×).',
        threshold: 'Пороговый сигнал — абсолютный сигнал мощности: Выкл., 0,50, 1,00, 2,00, 5,00 или 10,0 µSv/h.',
    },
    activity: {
        title: 'Необязательная запись активности',
        intro: 'Для живого экрана ничего записывать не нужно. Запись необязательна и создаёт обычную активность Garmin в Garmin Connect.',
        start: 'На экране измерений нажмите START (или угол Start/Активность на сенсоре). Подтвердите «Начать активность?». Шапка переключается на REC и таймер.',
        gps: 'Если «Записывать GPS» включено, часы ищут фикс. Можно увидеть «GPS…», затем «GPS».',
        live: 'Во время записи подвал показывает накопленную полную дозу этой сессии.',
        stopTitle: 'Меню остановки',
        save: 'Завершает сессию и пишет FIT на часы (затем синхронизация в Garmin Connect).',
        discard: 'Завершить без сохранения.',
        continue: 'Закрыть меню и продолжить запись.',
        fitTitle: 'Что сохраняется',
        fitCpm: 'CPM — скорость счёта во времени (график в Garmin Connect).',
        fitRate: 'Мощность дозы в µSv/h во времени (график).',
        fitTotal: 'Полная доза в µSv за всю сессию (сводка).',
        fitLap: 'Доза круга в µSv (на круг; автокруг часов, напр. 1 км, если включён).',
        fitGps: 'Трек GPS только если GPS было включено при старте.',
        close: 'Если закрыть приложение во время сессии, оно сохранит активность, чтобы данные не пропали.',
    },
    instinct: {
        title: 'Заметки Instinct (MIP)',
        buttons: 'Нет сенсорного экрана. Используйте подсказки: Меню, Подключить / Start, Назад.',
        connect: 'Подключение может быть дольше, чем на AMOLED. Оставайтесь на карточке подключения до первого значения.',
        chart: 'Импульсный график инвертирован на монохроме, чтобы волна читалась.',
        time: 'Время (и GPS / REC во время активности) использует маленький субдисплей.',
    },
    supportBefore: 'Вопросы или проблемы? Используйте',
    supportLink: 'форму обратной связи',
    supportAfter: '.',
};
export const radiacodeUk = {
    intro: {
        title: 'Що таке Radia Code App?',
        text: 'Radia Code App — застосунок Garmin Connect IQ, який напряму через Bluetooth Low Energy зв’язується з дозиметром RadiaCode. Для живих показів не потрібні телефон і інтернет. Ви бачите потужність дози, швидкість лічби, імпульсний графік відносно локального фону, температуру приладу і за бажанням можете записати активність Garmin з даними про радіацію.',
        lang: 'Застосунок наслідує мову годинника. Налаштування, змінені на годиннику, лишаються на годиннику.',
    },
    connect: {
        title: 'Підключити RadiaCode',
        warning: 'RadiaCode може бути підключений лише до одного пристрою одночасно. Телефон теж рахується. Якщо підключено офіційний застосунок RadiaCode (чи інший Bluetooth-клієнт), годинник не зможе використати прилад. Спочатку закрийте або відключіть його, потім спробуйте з годинника.',
        warningExtra: 'Те саме стосується іншого годинника, комп’ютера чи планшета. RadiaCode не повинен бути спарений деінде, перш ніж підключати його тут.',
        stepsTitle: 'Як спарити',
        step1: 'Увімкніть RadiaCode і тримайте його поруч із годинником (вистачить кількох метрів).',
        step2: 'Відкрийте Radia Code App на годиннику. Сканування починається одразу і показує «Пошук…».',
        step3: 'Коли прилад з’явиться в списку, оберіть його ВГОРУ / ВНИЗ і натисніть START або SELECT. На сенсорному екрані можна торкнутися назви.',
        step4: 'Годинник лишається на «Підключення…», доки не надійде перше живе значення. Це може зайняти кілька секунд, особливо на Instinct. Не натискайте START під час підключення.',
        step5: 'Після успішного підключення назва зберігається. Наступного разу застосунок підключиться сам.',
        forget: 'Для іншого приладу відкрийте меню і виберіть Забути пристрій. Це вимикає автопідключення. Повторне сканування запускає новий пошук.',
        notFound: 'Якщо прилад не з’являється або підключення не вдається, його майже завжди ще тримає телефонний застосунок або інший Bluetooth-клієнт. Відключіть там, зачекайте і скануйте знову.',
    },
    search: {
        title: 'Екран пошуку',
        titleLine: 'У заголовку показано «Radia Code».',
        list: 'Нижче список найближчих пристроїв RadiaCode, знайдених по Bluetooth.',
        instinct: 'На Instinct (MIP) підказки кнопок на корпусі: Меню ліворуч по центру, Підключити на START, Назад праворуч унизу.',
        empty: 'Під час сканування видно «Пошук…». Нічого немає — повторне сканування в меню.',
    },
    measure: {
        title: 'Екран вимірювань',
        intro: 'Щойно надходить перший відлік, живий екран замінює картку підключення.',
        dose: 'Велике значення потужності дози в обраних одиницях (зиверт або рентген, авто / µSv/h / nSv/h).',
        chart: 'Імпульсний графік нещодавньої швидкості лічби. Заливка відносно локального фону, щоб зростання було видно.',
        counts: 'Швидкість лічби як CPS (лічби на секунду) або CPM (лічби на хвилину).',
        footer: 'Підвал: температура RadiaCode і виявлений локальний фон.',
        time: 'Поточний час у шапці (на Instinct — на субдисплеї).',
        ring: 'На круглих кольорових екранах кільце (або невелика смуга на інших формах) порівнює поточну потужність із фоном.',
        colour: 'Колір графіка і значення залежить від потужності відносно фону. Автовизначення вважає приблизно 2× фону підвищеним і може сигналізувати.',
        rec: 'Під час активності шапка показує REC і час замість годинника, підвал — накопичену повну дозу.',
    },
    controls: {
        title: 'Кнопки (підключено)',
        menu: 'Відкриває налаштування (одиниці, GPS, екран, сигнали). На екрані пошуку та сама кнопка відкриває меню сканування.',
        start: 'Старт або стоп необов’язкової активності (START/SELECT або натисніть Start/Stop справа зверху). Натискання в іншому місці екрана вимірювань нічого не робить. Заблоковано під час підключення.',
        back: 'Вийти із застосунку. Якщо йде запис, спочатку меню зупинки (Зберегти / Скасувати / Продовжити).',
        select: 'На екрані пошуку: підключити вибраний пристрій.',
    },
    menu: {
        title: 'Пункти меню',
        scanTitle: 'Меню пошуку / сканування',
        scanIntro: 'Доступне, поки ви не підключені. Заголовок — назва застосунку.',
        forget: 'Забути пристрій — вимикає автопідключення і видаляє збережений прилад (якщо був).',
        rescan: 'Сканувати знову — новий пошук Bluetooth.',
        connectedTitle: 'Меню в підключеному стані',
        connectedIntro: 'Ті самі налаштування, що нижче, плюс Відключити, щоб відпустити поточний прилад і повернутися до списку.',
        settingsTitle: 'Налаштування (обидва меню)',
        dose: 'Одиниця дози — зиверт (Sv) або рентген (R).',
        rate: 'Одиниця потужності — Авто, µSv/h або nSv/h.',
        count: 'Одиниця лічби — CPS або CPM.',
        gps: 'Записувати GPS під час активності — увімк.: трек GPS. Вимк.: сесія indoor / тренування без GPS.',
        keep: 'Тримати екран активним — дисплей / підсвітка увімкнені, щоб бачити потужність, не будячи годинник.',
        auto: 'Автовизначення випромінювання — сигнал, якщо потужність явно вища за фон (близько 2×).',
        threshold: 'Пороговий сигнал — абсолютний сигнал потужності: Вимк., 0,50, 1,00, 2,00, 5,00 або 10,0 µSv/h.',
    },
    activity: {
        title: 'Необов’язковий запис активності',
        intro: 'Для живого екрана нічого записувати не потрібно. Запис необов’язковий і створює звичайну активність Garmin у Garmin Connect.',
        start: 'На екрані вимірювань натисніть START (або кут Start/Активність на сенсорі). Підтвердіть «Почати активність?». Шапка перемикається на REC і таймер.',
        gps: 'Якщо «Записувати GPS» увімкнено, годинник шукає фікс. Можна побачити «GPS…», потім «GPS».',
        live: 'Під час запису підвал показує накопичену повну дозу цієї сесії.',
        stopTitle: 'Меню зупинки',
        save: 'Завершує сесію і пише FIT на годинник (потім синхронізація в Garmin Connect).',
        discard: 'Завершити без збереження.',
        continue: 'Закрити меню і продовжити запис.',
        fitTitle: 'Що зберігається',
        fitCpm: 'CPM — швидкість лічби в часі (графік у Garmin Connect).',
        fitRate: 'Потужність дози в µSv/h у часі (графік).',
        fitTotal: 'Повна доза в µSv за всю сесію (підсумок).',
        fitLap: 'Доза кола в µSv (на коло; автоколо годинника, напр. 1 км, якщо увімкнено).',
        fitGps: 'Трек GPS лише якщо GPS було увімкнено на старті.',
        close: 'Якщо закрити застосунок під час сесії, він збереже активність, щоб дані не зникли.',
    },
    instinct: {
        title: 'Нотатки Instinct (MIP)',
        buttons: 'Немає сенсорного екрана. Використовуйте підказки: Меню, Підключити / Start, Назад.',
        connect: 'Підключення може бути довше, ніж на AMOLED. Залишайтеся на картці підключення до першого значення.',
        chart: 'Імпульсний графік інвертований на монохромі, щоб хвиля читалася.',
        time: 'Час (і GPS / REC під час активності) використовує маленький субдисплей.',
    },
    supportBefore: 'Питання чи проблеми? Скористайтеся',
    supportLink: 'формою зворотного зв’язку',
    supportAfter: '.',
};
export const radiacodeNo = {
    intro: {
        title: 'Hva er Radia Code App?',
        text: 'Radia Code App er en Garmin Connect IQ-klokkeapp som snakker direkte med en RadiaCode-strålingsmåler over Bluetooth Low Energy. Live-avlesninger trenger verken telefon eller internett. Du ser dosehastighet, telletakt, et pulsgraf mot lokal bakgrunn, enhetens temperatur, og du kan valgfritt ta opp en Garmin-aktivitet med strålingsdata.',
        lang: 'Appen følger klokkens språk. Innstillinger du endrer på klokken, blir på klokken.',
    },
    connect: {
        title: 'Koble til RadiaCode',
        warning: 'En RadiaCode kan bare være tilkoblet én enhet om gangen. Telefonen teller. Hvis den offisielle RadiaCode-appen (eller en annen Bluetooth-klient) er tilkoblet, kan ikke klokken bruke måleren. Lukk eller koble fra den appen først, og prøv deretter fra klokken.',
        warningExtra: 'Det samme gjelder en annen klokke, en datamaskin eller et nettbrett. RadiaCode skal ikke være paret andre steder før du kobler den til her.',
        stepsTitle: 'Slik parer du',
        step1: 'Slå på RadiaCode og hold den nær klokken (noen meter holder).',
        step2: 'Åpne Radia Code App på klokken. Den starter skanning med en gang og viser «Søker…».',
        step3: 'Når måleren vises i listen, marker den med OPP / NED og trykk START eller SELECT. På berøringsskjerm kan du trykke på navnet.',
        step4: 'Klokken blir på «Kobler til…» til den første live-verdien kommer. Det kan ta flere sekunder, særlig på Instinct. Ikke trykk START mens den kobler til.',
        step5: 'Etter vellykket tilkobling lagres navnet. Neste gang kobler appen til automatisk.',
        forget: 'For en annen måler: åpne menyen og velg Glem enhet. Det slår av auto-tilkobling. Skann på nytt starter et nytt søk.',
        notFound: 'Hvis måleren ikke vises eller tilkobling feiler, holder nesten alltid telefonappen eller en annen Bluetooth-klient den. Koble fra der, vent litt og skann på nytt.',
    },
    search: {
        title: 'Søkeskjerm',
        titleLine: 'Tittellinjen viser «Radia Code».',
        list: 'Under er listen over nærliggende RadiaCode-enheter funnet over Bluetooth.',
        instinct: 'På Instinct (MIP) sitter knappehint på kassen: Meny midt til venstre, Koble til på START, Tilbake nede til høyre.',
        empty: 'Under skanning ser du «Søker…». Ingenting funnet? Skann på nytt fra menyen.',
    },
    measure: {
        title: 'Måleskjerm',
        intro: 'Når første prøve kommer, erstatter live-visningen tilkoblingskortet.',
        dose: 'Stor dosehastighetsverdi med valgt enhet (sievert eller røntgen, auto / µSv/h / nSv/h).',
        chart: 'Pulsgraf av nylig telletakt. Fyllingen er mot lokal bakgrunn slik at en økning synes.',
        counts: 'Telletakt som CPS (tellinger per sekund) eller CPM (tellinger per minutt).',
        footer: 'Bunntekst: RadiaCode-temperatur og oppdaget lokal bakgrunn.',
        time: 'Gjeldende klokkeslett vises i hodet (på Instinct i underdisplayet).',
        ring: 'På runde fargeskjermer viser en nivåring (eller en liten stolpe på andre former) hvordan gjeldende takt står mot bakgrunnen.',
        colour: 'Farge på graf og verdi følger takten mot bakgrunnen. Autodeteksjon behandler omtrent 2× bakgrunn som forhøyet og kan varsle.',
        rec: 'Under en aktivitet viser hodet REC og løpende tid i stedet for klokken; bunnen den oppsamlede totaldosen.',
    },
    controls: {
        title: 'Knapper (tilkoblet)',
        menu: 'Åpner innstillinger (enheter, GPS, skjerm, alarmer). På søkeskjermen åpner samme knapp skannemenyen.',
        start: 'Start eller stopp den valgfrie aktiviteten (START/SELECT, eller trykk Start/Stopp øverst til høyre). Trykk andre steder på måleskjermen gjør ingenting. Blokkert under tilkobling.',
        back: 'Forlat appen. Hvis et opptak kjører, vises stoppmenyen først (Lagre / Forkast / Fortsett).',
        select: 'På søkeskjermen: koble til den merkede enheten.',
    },
    menu: {
        title: 'Menypunkter',
        scanTitle: 'Søke- / skannemeny',
        scanIntro: 'Tilgjengelig mens du ikke er tilkoblet. Tittelen er appnavnet.',
        forget: 'Glem enhet — slår av auto-tilkobling og sletter lagret måler (hvis det var en).',
        rescan: 'Skann på nytt — nytt Bluetooth-søk.',
        connectedTitle: 'Meny tilkoblet',
        connectedIntro: 'Samme innstillinger som under, pluss Koble fra for å slippe gjeldende måler og gå tilbake til listen.',
        settingsTitle: 'Innstillinger (begge menyer)',
        dose: 'Doseenhet — sievert (Sv) eller røntgen (R).',
        rate: 'Taktenhet — Auto, µSv/h eller nSv/h.',
        count: 'Telleenhet — CPS eller CPM.',
        gps: 'Logg GPS-posisjon under aktivitet — på: GPS-spor. Av: innendørs / trening uten GPS.',
        keep: 'Hold skjermen aktiv — skjerm / baklys på slik at du ser takten uten å vekke klokken.',
        auto: 'Autodeteksjon av stråling — varsel når takten er klart over bakgrunnen (omtrent 2×).',
        threshold: 'Terskelalarm — absolutt dosehastighetsalarm: Av, 0,50, 1,00, 2,00, 5,00 eller 10,0 µSv/h.',
    },
    activity: {
        title: 'Valgfri aktivitetsopptak',
        intro: 'Du trenger ikke ta opp noe for å bruke live-visningen. Opptak er valgfritt og lager en vanlig Garmin-aktivitet i Garmin Connect.',
        start: 'På måleskjermen trykk START (eller hjørnet Start/Aktivitet på berøring). Bekreft «Starte aktivitet?». Hodet bytter til REC og en timer.',
        gps: 'Hvis «Logg GPS-posisjon» er på, søker klokken et fix. Du kan se «GPS…» og deretter «GPS».',
        live: 'Under opptak viser bunnen den oppsamlede totaldosen for denne økten.',
        stopTitle: 'Stoppmeny',
        save: 'Avslutt økten og skriv FIT-filen til klokken (deretter synk til Garmin Connect).',
        discard: 'Avslutt uten å lagre.',
        continue: 'Lukk menyen og fortsett opptaket.',
        fitTitle: 'Hva som lagres',
        fitCpm: 'CPM — telletakt over tid (graf i Garmin Connect).',
        fitRate: 'Dosehastighet i µSv/h over tid (graf).',
        fitTotal: 'Total dose i µSv for hele økten (sammendrag).',
        fitLap: 'Rundedose i µSv (per runde; klokkens auto-runde, f.eks. 1 km, hvis på).',
        fitGps: 'GPS-spor bare hvis GPS-innstillingen var på ved start.',
        close: 'Hvis du lukker appen under en økt, lagrer den aktiviteten slik at dataene ikke går tapt.',
    },
    instinct: {
        title: 'Instinct (MIP)-merknader',
        buttons: 'Ingen berøringsskjerm. Bruk hintene: Meny, Koble til / Start, Tilbake.',
        connect: 'Tilkobling kan ta lenger tid enn på AMOLED. Bli på tilkoblingskortet til første verdi.',
        chart: 'Pulsgrafen er invertert på den monokrome skjermen slik at bølgen er lesbar.',
        time: 'Tid (og GPS / REC under aktivitet) bruker det lille underdisplayet.',
    },
    supportBefore: 'Spørsmål eller problemer? Bruk',
    supportLink: 'kontaktskjemaet',
    supportAfter: '.',
};
export const radiacodeSv = {
    intro: {
        title: 'Vad är Radia Code App?',
        text: 'Radia Code App är en Garmin Connect IQ-klockapp som pratar direkt med en RadiaCode-strålningsmätare över Bluetooth Low Energy. Livevärden behöver varken telefon eller internet. Du ser doshastighet, räknetakt, ett pulsgraf mot lokal bakgrund, enhetens temperatur och kan valfritt spela in en Garmin-aktivitet med strålningsdata.',
        lang: 'Appen följer klockans språk. Inställningar du ändrar på klockan stannar på klockan.',
    },
    connect: {
        title: 'Anslut din RadiaCode',
        warning: 'En RadiaCode kan bara vara ansluten till en enhet åt gången. Telefonen räknas. Om den officiella RadiaCode-appen (eller en annan Bluetooth-klient) är ansluten kan klockan inte använda mätaren. Stäng eller koppla från den appen först och försök sedan från klockan.',
        warningExtra: 'Samma sak gäller en annan klocka, en dator eller en surfplatta. RadiaCode ska inte vara parkopplad någon annanstans innan du ansluter den här.',
        stepsTitle: 'Så parkopplar du',
        step1: 'Slå på RadiaCode och håll den nära klockan (några meter räcker).',
        step2: 'Öppna Radia Code App på klockan. Den börjar skanna direkt och visar ”Söker…”.',
        step3: 'När mätaren syns i listan, markera den med UPP / NER och tryck START eller SELECT. På en pekskärm kan du trycka på namnet.',
        step4: 'Klockan stannar på ”Ansluter…” tills det första livevärdet kommer. Det kan ta flera sekunder, särskilt på Instinct. Tryck inte START medan den ansluter.',
        step5: 'Efter en lyckad anslutning sparas namnet. Nästa gång ansluter appen automatiskt.',
        forget: 'För en annan mätare: öppna menyn och välj Glöm enhet. Det stänger av autoanslutning. Skanna igen startar en ny sökning.',
        notFound: 'Om mätaren inte syns eller anslutningen misslyckas håller nästan alltid telefonappen eller en annan Bluetooth-klient den. Koppla från där, vänta en stund och skanna igen.',
    },
    search: {
        title: 'Sökskärm',
        titleLine: 'Titelraden visar ”Radia Code”.',
        list: 'Därunder listan över närliggande RadiaCode-enheter som hittats via Bluetooth.',
        instinct: 'På Instinct (MIP) sitter knapphintsen på höljet: Meny mitt till vänster, Anslut på START, Tillbaka nere till höger.',
        empty: 'Under skanning ser du ”Söker…”. Inget hittat? Skanna igen från menyn.',
    },
    measure: {
        title: 'Mätskärm',
        intro: 'När det första provet kommer ersätter livevyn anslutningskortet.',
        dose: 'Stort doshastighetsvärde med vald enhet (sievert eller röntgen, auto / µSv/h / nSv/h).',
        chart: 'Pulsgraf över den senaste räknetakten. Fyllnaden ligger mot den lokala bakgrunden så att en ökning syns.',
        counts: 'Räknetakt som CPS (räkningar per sekund) eller CPM (räkningar per minut).',
        footer: 'Sidfot: RadiaCode-temperatur och detekterad lokal bakgrund.',
        time: 'Aktuell tid visas i huvudet (på Instinct i underdisplayen).',
        ring: 'På runda färgskärmar visar en nivåring (eller en liten stapel på andra former) hur aktuell takt står mot bakgrunden.',
        colour: 'Färg på graf och värde följer takten mot bakgrunden. Autodetektering behandlar ungefär 2× bakgrund som förhöjd och kan larma.',
        rec: 'Under en aktivitet visar huvudet REC och förfluten tid i stället för klockan; sidfoten den ackumulerade totaldosen.',
    },
    controls: {
        title: 'Knappar (ansluten)',
        menu: 'Öppnar inställningar (enheter, GPS, skärm, larm). På sökskärmen öppnar samma knapp skanningsmenyn.',
        start: 'Starta eller stoppa den valfria aktiviteten (START/SELECT, eller tryck Start/Stopp uppe till höger). Tryck någon annanstans på mätskärmen gör ingenting. Blockerat under anslutning.',
        back: 'Lämna appen. Om en inspelning pågår visas stoppmenyn först (Spara / Kasta / Fortsätt).',
        select: 'På sökskärmen: anslut den markerade enheten.',
    },
    menu: {
        title: 'Menyval',
        scanTitle: 'Sök- / skanningsmeny',
        scanIntro: 'Tillgänglig när du inte är ansluten. Titeln är appnamnet.',
        forget: 'Glöm enhet — stänger av autoanslutning och rensar sparad mätare (om det fanns en).',
        rescan: 'Skanna igen — ny Bluetooth-sökning.',
        connectedTitle: 'Meny ansluten',
        connectedIntro: 'Samma inställningar som nedan, plus Koppla från för att släppa aktuell mätare och gå tillbaka till listan.',
        settingsTitle: 'Inställningar (båda menyer)',
        dose: 'Dosenhet — sievert (Sv) eller röntgen (R).',
        rate: 'Taktenhet — Auto, µSv/h eller nSv/h.',
        count: 'Räkningsenhet — CPS eller CPM.',
        gps: 'Logga GPS-position under aktivitet — på: GPS-spår. Av: inomhus / träning utan GPS.',
        keep: 'Håll skärmen aktiv — skärm / bakljus på så att du ser takten utan att väcka klockan.',
        auto: 'Autodetektera strålning — varning när takten är tydligt över bakgrunden (ungefär 2×).',
        threshold: 'Tröskellarm — absolut doshastighetslarm: Av, 0,50, 1,00, 2,00, 5,00 eller 10,0 µSv/h.',
    },
    activity: {
        title: 'Valfri aktivitetsinspelning',
        intro: 'Du behöver inte spela in något för att använda livevyn. Inspelning är valfri och skapar en vanlig Garmin-aktivitet i Garmin Connect.',
        start: 'På mätskärmen tryck START (eller hörnet Start/Aktivitet på pekskärm). Bekräfta ”Starta aktivitet?”. Huvudet växlar till REC och en timer.',
        gps: 'Om ”Logga GPS-position” är på söker klockan ett fix. Du kan se ”GPS…” och sedan ”GPS”.',
        live: 'Under inspelning visar sidfoten den ackumulerade totaldosen för sessionen.',
        stopTitle: 'Stoppmeny',
        save: 'Avsluta sessionen och skriv FIT-filen till klockan (sedan synk till Garmin Connect).',
        discard: 'Avsluta utan att spara.',
        continue: 'Stäng menyn och fortsätt spela in.',
        fitTitle: 'Vad som lagras',
        fitCpm: 'CPM — räknetakt över tid (graf i Garmin Connect).',
        fitRate: 'Doshastighet i µSv/h över tid (graf).',
        fitTotal: 'Total dos i µSv för hela sessionen (sammanfattning).',
        fitLap: 'Varvdos i µSv (per varv; klockans autovarv, t.ex. 1 km, om på).',
        fitGps: 'GPS-spår bara om GPS-inställningen var på vid start.',
        close: 'Om du stänger appen under en session sparar den aktiviteten så att data inte går förlorade.',
    },
    instinct: {
        title: 'Instinct (MIP)-anteckningar',
        buttons: 'Ingen pekskärm. Använd hintsen: Meny, Anslut / Start, Tillbaka.',
        connect: 'Anslutning kan ta längre tid än på AMOLED. Stanna på anslutningskortet tills första värdet.',
        chart: 'Pulsgrafen är inverterad på den monokroma skärmen så att vågen är läsbar.',
        time: 'Tid (och GPS / REC under aktivitet) använder den lilla underdisplayen.',
    },
    supportBefore: 'Frågor eller problem? Använd',
    supportLink: 'kontaktformuläret',
    supportAfter: '.',
};
export const radiacodeJa = {
    intro: {
        title: 'Radia Code App とは？',
        text: 'Radia Code App は、Bluetooth Low Energy で RadiaCode 放射線計と直接通信する Garmin Connect IQ ウォッチアプリです。ライブ表示にスマホやネットは不要です。線量率、計数率、局所バックグラウンドに対するパルスチャート、本体温度を表示し、任意で放射線データを含む Garmin アクティビティを記録できます。',
        lang: 'アプリは時計の言語に従います。時計で変更した設定は時計に保存されます。',
    },
    connect: {
        title: 'RadiaCode を接続する',
        warning: 'RadiaCode は同時に1台の機器にしか接続できません。スマホも含まれます。公式 RadiaCode アプリ（または他の Bluetooth クライアント）が接続中だと、時計は使えません。先にそちらを終了または切断してから、時計で再試行してください。',
        warningExtra: '別の時計、パソコン、タブレットも同じです。ここで接続する前に、他の機器とのペアリングを外してください。',
        stepsTitle: 'ペアリング手順',
        step1: 'RadiaCode の電源を入れ、時計の近くに置きます（数メートルで十分です）。',
        step2: '時計で Radia Code App を開きます。すぐにスキャンを始め、「検索中…」と表示します。',
        step3: 'リストに表示されたら、上／下で選び、START または SELECT を押します。タッチ画面では名前をタップできます。',
        step4: '最初のライブ値が来るまで時計は「接続中…」のままです。Instinct では数秒かかることがあります。接続中は START を押さないでください。',
        step5: '接続に成功すると名前が保存されます。次回起動時は自動で再接続します。',
        forget: '別の計器にする場合はメニューで「デバイスを忘れる」を選びます。自動接続がオフになります。「再スキャン」で新しい検索を始めます。',
        notFound: '計器が出ない、または接続に失敗する場合、ほぼ必ずスマホアプリや他の Bluetooth クライアントが保持しています。そちらで切断し、少し待ってから再スキャンしてください。',
    },
    search: {
        title: '検索画面',
        titleLine: 'タイトルバーには「Radia Code」と表示されます。',
        list: 'その下に、Bluetooth で見つかった近くの RadiaCode の一覧があります。',
        instinct: 'Instinct（MIP）ではベゼルにヒントがあります。左中央がメニュー、START が接続、右下が戻るです。',
        empty: 'スキャン中は「検索中…」と出ます。見つからない場合はメニューから再スキャンします。',
    },
    measure: {
        title: '測定画面',
        intro: '最初のサンプルが来ると、ライブ表示が接続カードに代わります。',
        dose: '選んだ単位（シーベルトまたはレントゲン、自動 / µSv/h / nSv/h）の大きな線量率。',
        chart: '直近の計数率のパルスチャート。塗りは局所バックグラウンド基準なので上昇が見やすいです。',
        counts: '計数率は CPS（毎秒）または CPM（毎分）です。',
        footer: 'フッター：RadiaCode の温度と検出した局所バックグラウンド。',
        time: '現在時刻はヘッダーに表示されます（Instinct ではサブディスプレイ）。',
        ring: '丸いカラー画面ではレベルリング（他形状では小さなバー）が現在の率とバックグラウンドを比較します。',
        colour: 'チャートと値の色はバックグラウンド相対の率に従います。自動検出はおよそ2倍を上昇とみなし、警告できます。',
        rec: 'アクティビティ記録中、ヘッダーは時計の代わりに REC と経過時間、フッターは累積総線量です。',
    },
    controls: {
        title: 'ボタン（接続中）',
        menu: '設定（単位、GPS、画面、アラーム）を開きます。検索画面では同じボタンがスキャンメニューを開きます。',
        start: '任意のアクティビティを開始／停止します（START/SELECT、または右上の Start/Stop をタップ）。測定画面の他の場所をタップしても何も起きません。接続中は無効です。',
        back: 'アプリを終了します。記録中なら先に停止メニュー（保存 / 破棄 / 続行）が出ます。',
        select: '検索画面：選択中のデバイスに接続します。',
    },
    menu: {
        title: 'メニュー項目',
        scanTitle: '検索／スキャンメニュー',
        scanIntro: '未接続のときに使えます。タイトルはアプリ名です。',
        forget: 'デバイスを忘れる — 自動接続をオフにし、保存した計器を消します（保存がある場合）。',
        rescan: '再スキャン — 新しい Bluetooth 検索を開始します。',
        connectedTitle: '接続中メニュー',
        connectedIntro: '下記と同じ設定に加え、切断で現在の計器を外してリストに戻ります。',
        settingsTitle: '設定（両方のメニュー）',
        dose: '線量単位 — シーベルト（Sv）またはレントゲン（R）。',
        rate: '率の単位 — 自動、µSv/h、または nSv/h。',
        count: '計数単位 — CPS または CPM。',
        gps: 'アクティビティ中に GPS 位置を記録 — オン：GPS 軌跡。オフ：屋内／トレーニング（GPS なし）。',
        keep: '画面を常時点灯 — 時計を起こさずに率を見られるよう、画面／バックライトを維持します。',
        auto: '放射線の自動検出 — 率がバックグラウンドより明らかに高い（約2倍）ときに警告します。',
        threshold: 'しきい値アラーム — 絶対線量率アラーム：オフ、0.50、1.00、2.00、5.00、または 10.0 µSv/h。',
    },
    activity: {
        title: '任意のアクティビティ記録',
        intro: 'ライブ表示に記録は不要です。記録は任意で、Garmin Connect に通常の Garmin アクティビティを作ります。',
        start: '測定画面で START を押します（タッチでは Start/アクティビティ角）。「アクティビティを開始しますか？」を確認します。ヘッダーが REC とタイマーになります。',
        gps: '「アクティビティ中に GPS を記録」がオンなら、時計は測位します。「GPS…」のあと「GPS」と出ることがあります。',
        live: '記録中、フッターはこのセッションの累積総線量を表示します。',
        stopTitle: '停止メニュー',
        save: 'セッションを終了し、FIT を時計に書き込みます（その後 Garmin Connect に同期）。',
        discard: '保存せずに終了します。',
        continue: 'メニューを閉じて記録を続けます。',
        fitTitle: '保存される内容',
        fitCpm: 'CPM — 時間経過の計数率（Garmin Connect のチャート）。',
        fitRate: 'µSv/h の線量率の時間経過（チャート）。',
        fitTotal: 'セッション全体の総線量 µSv（要約）。',
        fitLap: 'ラップ線量 µSv（ラップごと。時計のオートラップ、例：1 km が有効な場合）。',
        fitGps: '開始時に GPS 設定がオンだった場合のみ GPS 軌跡。',
        close: '記録中にアプリを閉じると、データが失われないようアクティビティを保存します。',
    },
    instinct: {
        title: 'Instinct（MIP）の注意',
        buttons: 'タッチ画面はありません。ヒントを使います：メニュー、接続／Start、戻る。',
        connect: 'AMOLED より接続に時間がかかることがあります。最初の値が出るまで接続カードにいてください。',
        chart: 'モノクロ表示ではパルスチャートを反転して波形を読みやすくしています。',
        time: '時刻（およびアクティビティ中の GPS / REC）は小さなサブディスプレイを使います。',
    },
    supportBefore: 'ご質問や不具合は',
    supportLink: 'お問い合わせフォーム',
    supportAfter: 'をご利用ください。',
};
export const radiacodeKo = {
    intro: {
        title: 'Radia Code App이란?',
        text: 'Radia Code App은 Bluetooth Low Energy로 RadiaCode 방사선 측정기와 직접 통신하는 Garmin Connect IQ 워치 앱입니다. 실시간 표시에는 휴대폰이나 인터넷이 필요 없습니다. 선량률, 계수율, 지역 배경 대비 펄스 차트, 기기 온도를 보고, 원하면 방사선 데이터가 포함된 Garmin 활동을 기록할 수 있습니다.',
        lang: '앱은 워치 언어를 따릅니다. 워치에서 바꾼 설정은 워치에 저장됩니다.',
    },
    connect: {
        title: 'RadiaCode 연결',
        warning: 'RadiaCode는 한 번에 한 기기만 연결할 수 있습니다. 휴대폰도 포함됩니다. 공식 RadiaCode 앱(또는 다른 Bluetooth 클라이언트)이 연결되어 있으면 워치가 측정기를 쓸 수 없습니다. 먼저 그 앱을 종료하거나 끊은 뒤 워치에서 다시 시도하세요.',
        warningExtra: '다른 워치, 컴퓨터, 태블릿도 같습니다. 여기서 연결하기 전에 다른 곳의 페어링을 해제하세요.',
        stepsTitle: '페어링 방법',
        step1: 'RadiaCode를 켜고 워치 가까이 두세요(몇 미터면 충분합니다).',
        step2: '워치에서 Radia Code App을 엽니다. 바로 검색을 시작하고 “검색 중…”을 표시합니다.',
        step3: '목록에 나타나면 위/아래로 고르고 START 또는 SELECT를 누르세요. 터치스크린에서는 이름을 탭할 수 있습니다.',
        step4: '첫 실시간 값이 올 때까지 워치는 “연결 중…”에 머뭅니다. Instinct에서는 몇 초가 걸릴 수 있습니다. 연결 중에는 START를 누르지 마세요.',
        step5: '연결에 성공하면 이름이 저장됩니다. 다음에 열면 앱이 자동으로 다시 연결합니다.',
        forget: '다른 측정기를 쓰려면 메뉴에서 기기 잊기를 선택하세요. 자동 연결이 꺼집니다. 다시 스캔하면 새 검색이 시작됩니다.',
        notFound: '측정기가 안 보이거나 연결이 실패하면 거의 항상 휴대폰 앱이나 다른 Bluetooth 클라이언트가 잡고 있습니다. 거기서 끊고 잠시 기다린 뒤 다시 스캔하세요.',
    },
    search: {
        title: '검색 화면',
        titleLine: '제목 표시줄에 “Radia Code”가 나옵니다.',
        list: '아래에 Bluetooth로 찾은 근처 RadiaCode 목록이 있습니다.',
        instinct: 'Instinct(MIP)에서는 베젤에 힌트가 있습니다. 왼쪽 중간 메뉴, START 연결, 오른쪽 아래 뒤로.',
        empty: '검색 중에는 “검색 중…”이 보입니다. 없으면 메뉴에서 다시 스캔하세요.',
    },
    measure: {
        title: '측정 화면',
        intro: '첫 샘플이 오면 실시간 화면이 연결 카드를 대체합니다.',
        dose: '선택한 단위(시버트 또는 뢴트겐, 자동 / µSv/h / nSv/h)의 큰 선량률 값.',
        chart: '최근 계수율의 펄스 차트. 채움은 지역 배경 기준이라 상승이 잘 보입니다.',
        counts: '계수율은 CPS(초당) 또는 CPM(분당)입니다.',
        footer: '바닥글: RadiaCode 온도와 감지된 지역 배경.',
        time: '현재 시각은 헤더에 표시됩니다(Instinct는 서브 디스플레이).',
        ring: '둥근 컬러 화면에서는 레벨 링(다른 형태는 작은 막대)이 현재 율과 배경을 비교합니다.',
        colour: '차트와 값 색은 배경 대비 율을 따릅니다. 자동 감지는 대략 2배를 상승으로 보고 알릴 수 있습니다.',
        rec: '활동 기록 중 헤더는 시계 대신 REC와 경과 시간, 바닥글은 누적 총선량입니다.',
    },
    controls: {
        title: '버튼(연결됨)',
        menu: '설정(단위, GPS, 화면, 알람)을 엽니다. 검색 화면에서는 같은 버튼이 스캔 메뉴를 엽니다.',
        start: '선택 활동을 시작/중지합니다(START/SELECT 또는 오른쪽 위 Start/Stop 탭). 측정 화면의 다른 곳을 탭해도 아무 일도 없습니다. 연결 중에는 차단됩니다.',
        back: '앱을 나갑니다. 기록 중이면 먼저 중지 메뉴(저장 / 버리기 / 계속)가 나옵니다.',
        select: '검색 화면: 선택한 기기에 연결합니다.',
    },
    menu: {
        title: '메뉴 항목',
        scanTitle: '검색 / 스캔 메뉴',
        scanIntro: '연결되지 않았을 때 사용할 수 있습니다. 제목은 앱 이름입니다.',
        forget: '기기 잊기 — 자동 연결을 끄고 저장된 측정기를 지웁니다(있는 경우).',
        rescan: '다시 스캔 — 새 Bluetooth 검색을 시작합니다.',
        connectedTitle: '연결 중 메뉴',
        connectedIntro: '아래와 같은 설정에 더해, 연결 해제로 현재 측정기를 끊고 목록으로 돌아갑니다.',
        settingsTitle: '설정(두 메뉴 모두)',
        dose: '선량 단위 — 시버트(Sv) 또는 뢴트겐(R).',
        rate: '율 단위 — 자동, µSv/h 또는 nSv/h.',
        count: '계수 단위 — CPS 또는 CPM.',
        gps: '활동 중 GPS 위치 기록 — 켜짐: GPS 궤적. 꺼짐: 실내/훈련(GPS 없음).',
        keep: '화면 유지 — 워치를 깨우지 않고 율을 볼 수 있게 화면/백라이트를 켭니다.',
        auto: '방사선 자동 감지 — 율이 배경보다 분명히 높을 때(약 2배) 알립니다.',
        threshold: '임계 알람 — 절대 선량률 알람: 끔, 0.50, 1.00, 2.00, 5.00 또는 10.0 µSv/h.',
    },
    activity: {
        title: '선택적 활동 기록',
        intro: '실시간 표시를 위해 기록할 필요는 없습니다. 기록은 선택이며 Garmin Connect에 일반 Garmin 활동을 만듭니다.',
        start: '측정 화면에서 START를 누르세요(터치에서는 Start/활동 모서리). “활동을 시작할까요?”를 확인합니다. 헤더가 REC와 타이머로 바뀝니다.',
        gps: '“활동 중 GPS 기록”이 켜져 있으면 워치가 위치를 찾습니다. “GPS…” 다음에 “GPS”가 보일 수 있습니다.',
        live: '기록 중 바닥글은 이 세션의 누적 총선량을 보여 줍니다.',
        stopTitle: '중지 메뉴',
        save: '세션을 끝내고 FIT를 워치에 씁니다(이후 Garmin Connect로 동기화).',
        discard: '저장하지 않고 끝냅니다.',
        continue: '메뉴를 닫고 기록을 계속합니다.',
        fitTitle: '저장되는 내용',
        fitCpm: 'CPM — 시간에 따른 계수율(Garmin Connect 차트).',
        fitRate: '시간에 따른 µSv/h 선량률(차트).',
        fitTotal: '세션 전체 총선량 µSv(요약).',
        fitLap: '랩 선량 µSv(랩당. 워치 자동 랩, 예: 1 km가 켜진 경우).',
        fitGps: '시작 시 GPS 설정이 켜져 있었을 때만 GPS 궤적.',
        close: '세션 중에 앱을 닫으면 데이터가 사라지지 않도록 활동을 저장합니다.',
    },
    instinct: {
        title: 'Instinct(MIP) 참고',
        buttons: '터치스크린이 없습니다. 힌트를 쓰세요: 메뉴, 연결 / Start, 뒤로.',
        connect: 'AMOLED보다 연결이 더 걸릴 수 있습니다. 첫 값이 나올 때까지 연결 카드에 있으세요.',
        chart: '흑백 화면에서는 파형이 읽히도록 펄스 차트를 반전합니다.',
        time: '시각(및 활동 중 GPS / REC)은 작은 서브 디스플레이를 사용합니다.',
    },
    supportBefore: '질문이나 문제가 있으면',
    supportLink: '문의 양식',
    supportAfter: '을 이용해 주세요.',
};
export const radiacodeZh = {
    intro: {
        title: '什么是 Radia Code App？',
        text: 'Radia Code App 是一款 Garmin Connect IQ 手表应用，通过低功耗蓝牙直接连接 RadiaCode 辐射仪。实时读数不需要手机或网络。你可以看到剂量率、计数率、相对本地本底的脉冲图、设备温度，并可选择将辐射数据记录为 Garmin 活动。',
        lang: '应用跟随手表语言。你在手表上更改的设置保存在手表上。',
    },
    connect: {
        title: '连接 RadiaCode',
        warning: 'RadiaCode 一次只能连接一台设备。手机也算。如果官方 RadiaCode 手机应用（或其他蓝牙客户端）已连接，手表就无法使用该仪器。请先关闭或断开该应用，再在手表上重试。',
        warningExtra: '另一块手表、电脑或平板也一样。在这里连接之前，请先在其他地方取消配对。',
        stepsTitle: '如何配对',
        step1: '打开 RadiaCode，并靠近手表（几米即可）。',
        step2: '在手表上打开 Radia Code App。它会立即开始扫描并显示“正在搜索…”。',
        step3: '仪器出现在列表中后，用上/下选中，再按 START 或 SELECT。触摸屏可点按名称。',
        step4: '手表会停留在“正在连接…”，直到第一条实时数据到达。这可能需要几秒，尤其是 Instinct。连接过程中不要按 START。',
        step5: '连接成功后会保存设备名。下次打开应用会自动重连。',
        forget: '若要换另一台仪器，打开菜单并选择忘记设备。这会关闭自动连接。“重新扫描”开始新的搜索。',
        notFound: '如果仪器不出现或连接失败，几乎总是手机应用或其他蓝牙客户端还占着。先在那边断开，稍等再重新扫描。',
    },
    search: {
        title: '搜索界面',
        titleLine: '标题栏显示“Radia Code”。',
        list: '下方是通过蓝牙找到的附近 RadiaCode 设备列表。',
        instinct: '在 Instinct（MIP）上，按键提示在表圈：左中为菜单，START 为连接，右下为返回。',
        empty: '扫描时显示“正在搜索…”。若没有结果，请从菜单重新扫描。',
    },
    measure: {
        title: '测量界面',
        intro: '第一条样本到达后，实时画面会替换连接卡片。',
        dose: '按所选单位显示的大剂量率（希沃特或伦琴，自动 / µSv/h / nSv/h）。',
        chart: '近期计数率的脉冲图。填充相对本地本底绘制，便于看出上升。',
        counts: '计数率为 CPS（每秒）或 CPM（每分钟）。',
        footer: '页脚：RadiaCode 温度和检测到的本地本底。',
        time: '当前时间显示在页眉（Instinct 在副屏）。',
        ring: '圆形彩色屏幕用等级环（其他形状用小条）比较当前率与本底。',
        colour: '图表和数值颜色跟随相对本底的率。自动检测大约将 2 倍本底视为升高并可报警。',
        rec: '记录活动时，页眉显示 REC 和经过时间而不是时钟；页脚显示累计总剂量。',
    },
    controls: {
        title: '按键（已连接）',
        menu: '打开设置（单位、GPS、屏幕、警报）。在搜索界面，同一按键打开扫描菜单。',
        start: '开始或停止可选活动（START/SELECT，或点按右上角 Start/Stop）。点按测量界面其他位置无效。连接过程中禁用。',
        back: '退出应用。若正在记录，会先出现停止菜单（保存 / 丢弃 / 继续）。',
        select: '搜索界面：连接高亮的设备。',
    },
    menu: {
        title: '菜单项',
        scanTitle: '搜索 / 扫描菜单',
        scanIntro: '未连接时可用。标题是应用名称。',
        forget: '忘记设备 — 关闭自动连接并清除已保存的仪器（如有）。',
        rescan: '重新扫描 — 开始新的蓝牙搜索。',
        connectedTitle: '已连接菜单',
        connectedIntro: '与下方相同的设置，外加断开以释放当前仪器并返回列表。',
        settingsTitle: '设置（两个菜单）',
        dose: '剂量单位 — 希沃特（Sv）或伦琴（R）。',
        rate: '率单位 — 自动、µSv/h 或 nSv/h。',
        count: '计数单位 — CPS 或 CPM。',
        gps: '活动期间记录 GPS 位置 — 开：包含 GPS 轨迹。关：室内 / 训练，无 GPS。',
        keep: '保持屏幕常亮 — 保持显示 / 背光，以便不唤醒手表就能看率。',
        auto: '自动检测辐射 — 当率明显高于本底（约 2 倍）时提醒。',
        threshold: '阈值警报 — 绝对剂量率警报：关、0.50、1.00、2.00、5.00 或 10.0 µSv/h。',
    },
    activity: {
        title: '可选活动记录',
        intro: '使用实时显示不必记录任何内容。记录是可选的，会在 Garmin Connect 中生成普通 Garmin 活动。',
        start: '在测量界面按 START（触摸屏点按 Start/活动角）。确认“开始活动？”。页眉变为 REC 和计时器。',
        gps: '若“活动期间记录 GPS”已开，手表会寻找定位。你可能先看到“GPS…”，然后是“GPS”。',
        live: '记录时，页脚显示本场次累计总剂量。',
        stopTitle: '停止菜单',
        save: '结束场次并将 FIT 写入手表（随后同步到 Garmin Connect）。',
        discard: '结束且不保存。',
        continue: '关闭菜单并继续记录。',
        fitTitle: '保存的内容',
        fitCpm: 'CPM — 随时间变化的计数率（Garmin Connect 图表）。',
        fitRate: '随时间变化的 µSv/h 剂量率（图表）。',
        fitTotal: '整场总剂量 µSv（摘要）。',
        fitLap: '圈剂量 µSv（每圈；手表自动圈，如已开启的 1 km）。',
        fitGps: '仅当开始时 GPS 设置为开时才有 GPS 轨迹。',
        close: '若在记录中关闭应用，应用会保存活动，以免数据丢失。',
    },
    instinct: {
        title: 'Instinct（MIP）说明',
        buttons: '没有触摸屏。使用提示：菜单、连接 / Start、返回。',
        connect: '连接可能比 AMOLED 更久。请留在连接卡片上，直到出现第一个值。',
        chart: '单色屏上将脉冲图反相，以便看清波形。',
        time: '时间（以及活动中的 GPS / REC）使用小副屏。',
    },
    supportBefore: '有问题？请使用',
    supportLink: '联系表单',
    supportAfter: '。',
};
export const RADIOCODE_BY_LANG = {
    en: radiacodeEn,
    de: radiacodeDe,
    fr: radiacodeFr,
    es: radiacodeEs,
    it: radiacodeIt,
    pt: radiacodePt,
    nl: radiacodeNl,
    pl: radiacodePl,
    cs: radiacodeCs,
    ru: radiacodeRu,
    uk: radiacodeUk,
    no: radiacodeNo,
    sv: radiacodeSv,
    ja: radiacodeJa,
    ko: radiacodeKo,
    zh: radiacodeZh,
};
